import keyMirror from "key-mirror";

export const BASE_SUBDOMAINS = ["dev", "stg", "app"];

export const FOLDER_TYPES = {
  REGULAR: "REGULAR",
  TEMPLATE: "TEMPLATE",
};

export const COORDINATE = {
  DEFAULT_LATITUDE_DELTA: 44.976073,
  DEFAULT_LONGITUDE_DELTA: -93.272293,
};

export const DIMENSIONS = {
  FOOTER_HEIGHT: 60,
  NAVIGATION_BAR_HEIGHT: 80,
};

export const RESPONSIVE_BREAKPOINTS = {
  MOBILE_PORTRAIT: 375,
  MOBILE_LANDSCAPE: 667,

  TABLET_PORTRAIT: 768,
  TABLET_LANDSCAPE: 1024,

  LAPTOP: 1366,
};

export const ERROR_CODES = {
  INVALID_SSN: "invalid.ssn",
  INVALID_EMAIL: "invalid.email",
  INVALID_LOGIN: "invalid.login",
};

export const SERVER_ERROR_CODES = {
  NO_CONNECTION: "no.connection",
  CONNECTION_ABORTED: "ECONNABORTED",
  INTERNAL_SERVER_ERROR: "internal.server.error",
  SERVER_IS_NOT_AVAILABLE: "server.is.not.available",
  NO_CONNECTION_OR_SERVER_IS_NOT_AVAILABLE: "no.connection.or.server.is.not.available",

  INVALID_TOKEN: "token.invalid",
  INVALID_PASSWORD: "invalid.password",
  INVALID_LOGIN: "invalid.login",
  INVALID_COMPANY_ID: "invalid.company.id",

  NO_PHYSICIAN_FOUND: "physician.not.found",
  NO_PATIENT_INFO_FOUND: "patient.info.not.found",
  NO_PATIENTS_FOUND: "no.associated.patients.found",

  NO_PHONE_FOR_REGISTRATION: "no.phone.for.registration",
  NO_EMAIL_FOR_REGISTRATION: "no.email.for.registration",
  NO_PHONE_AND_EMAIL_FOR_REGISTRATION: "no.phone.and.email.for.registration",

  UNAUTHORIZED: "auth.authorized",
  BAD_CREDENTIALS: "auth.bad.credentials",
  EXPIRED_CREDENTIALS: "auth.credentials.expired",
  INVALID_PASSWORD_RESET_REQUEST: "auth.password.reset.request.invalid",

  ACCOUNT_LOCKED: "auth.account.locked",
  ACCOUNT_INACTIVE: "auth.account.inactive",
  ACCOUNT_NOT_FOUND: "auth.account.not.found",

  ACCESS_DENIED: "access.denied",

  SDOH_DATA_ERROR: "sdoh.data.error",
  SDOH_ZIP_NOT_DOWNLOADED: "sdoh.zip.not.downloaded",

  COMING_SOON: "coming.soon",
};

export let VALIDATION_ERROR_TEXTS = {
  EMPTY_FIELD: "Please fill in the required field",
  LENGTH_MINIMUM: "Please enter at least $0 characters",
  LENGTH_MAXIMUM: "Please enter not more than $0 characters",
  LENGTH_EQUAL: "Please enter $0 characters",
  EMAIL_FORMAT: "Please enter a valid email address",
  PHONE_FORMAT: "Please enter a valid phone number",
  FAX_FORMAT: "Please enter a valid fax number",
  NUMBER_FORMAT: "Only digits are allowed",
  ZIP_CODE_FORMAT: "The specified US zip code is not valid",
  NUMBER_FORMAT_SPECIFIC: "Please enter only $0 digits",
  NUMBER_LENGTH_MINIMUM: "Please enter at least $0 digit",
  NUMBER_LENGTH_MAXIMUM: "Please enter not more than $0 digit",
  CONFIRM_PASSWORD_MATCH: "The passwords you entered do not match",
  PASSWORD_COMPLEXITY: `The password you entered doesn't meet complexity requirements`,
  FILE_SIZE: "Please not upload file more than $0 MB",
  FILE_FORMAT: "This file type is not allowed",
  NON_UNIQ_SHORT: "The $0 with $1 entered already exists.",
  NON_UNIQ: "The $0 with $1 entered already exists in the system.",
  URL_FORMAT: "Please enter a valid url",
};

export const ENTITY_TYPES = keyMirror({
  EVENT: null,
  PHONE: null,
  PERSON: null,
  CLIENT: null,
  ADDRESS: null,
  LOCATION: null,
  CARE_MEMBER: null,
  PHYSICIAN_PROFESSIONAL: null,
  DRIVER_LICENCE_NUMBER: null,
});

export let MAX_FILE_NAME_LENGTH = 120;

export const AUTHENTICATION_EXCLUDED_PATHS = [
  "*/home*",
  "*/sign-in*",
  "*/invitation*",
  "*/old-password*",
  "*/sign-up/expired*",
  "*/create-password*",
  "*/reset-password*",
  "*/reset-password-request*",
  "*/care-team-invitations*",
  "*/documents/e-sign-requested*",
  "*/associate/:id/:type*",
  "*/associate/:id/:type/:name/login*",
  "*/associate/:id/:type/login/success*",
  "*/associate/:id/:type/:name/register*",
  "*/simplyplace*",
  "*/simplyplace/buildingdetail/:id*",
  "*/medication/referrals/:clientId/:medicationId/:intake/:smsId*",
  "*/mobile/medication/referrals/:clientId/:medicationId/:smsId*",
  "*/emailMedication*",
  "*/freemium-invitation*",
];

export const DOCU_TRACK = {
  MAX_FILE_SIZE_MB: 10,
};

export let ALLOWED_FILE_FORMATS = keyMirror({
  BIN: null,
  DOC: null,
  DOCX: null,
  PDF: null,
  XLS: null,
  XLSX: null,
  TXT: null,
  XML: null,
  JPG: null,
  JPEG: null,
  PJPG: null,
  PNG: null,
  GIF: null,
  ZIP: null,
  X_ZIP: null,
  X_ZIP_COMPRESSED: null,
  TIFF: null,
  AMR: null,
  MP3: null,
  MPEG: null,
  MP4: null,
  OTHER: null,
  CER: null,
  CERT: null,
  CRT: null,
  FOLDER: null,
});

export let FILE_SIZE_FORMATS = keyMirror({
  BYTE: null,
  KB: null,
  MB: null,
  GB: null,
});

export let VENDOR_ROLES = ["ROLE_VENDOR_CODE", "ROLE_PHARMACIST_VENDOR_CODE", "ROLE_DOCTOR_CODE"];
export let SYSTEM_ROLES = {
  CASE_MANAGER: "ROLE_CASE_MANAGER",
  CARE_COORDINATOR: "ROLE_CARE_COORDINATOR",
  PARENT_GUARDIAN: "ROLE_PARENT_GUARDIAN",
  PERSON_RECEIVING_SERVICES: "ROLE_PERSON_RECEIVING_SERVICES",
  PRIMARY_PHYSICIAN: "ROLE_PRIMARY_PHYSICIAN",
  BEHAVIORAL_HEALTH: "ROLE_BEHAVIORAL_HEALTH",
  COMMUNITY_MEMBERS: "ROLE_COMMUNITY_MEMBERS",
  SERVICE_PROVIDER: "ROLE_SERVICE_PROVIDER",
  ADMINISTRATOR: "ROLE_ADMINISTRATOR",
  SUPER_ADMINISTRATOR: "ROLE_SUPER_ADMINISTRATOR",
  COMMUNITY_ADMINISTRATOR: "ROLE_COMMUNITY_ADMINISTRATOR",
  COMMUNITY_PROVIDER: "ROLE_COMMUNITY_MEMBERS",
  NOTIFY_USER: "ROLE_NOTIFY_USER",
  PHARMACIST: "ROLE_PHARMACIST",
  PHARMACY_TECHNICIAN: "ROLE_PHARMACY_TECHNICIAN",
  NURSE: "ROLE_NURSE",
  EXTERNAL_PROVIDER: "ROLE_EXTERNAL_USER",
  HOME_CARE_ASSISTANT: "ROLE_HCA",
  CONTENT_CREATOR: "ROLE_CONTENT_CREATOR",
  TELE_HEALTH_NURSE: "ROLE_TELE_HEALTH_NURSE",
  ORGANIZATION_ADMIN: "ROLE_ORGANIZATION_ADMIN_CODE",
  QUALITY_ASSURANCE: "ROLE_QUALITY_ASSURANCE_CODE",
  NAVI_GUIDE: "ROLE_NAVI_GUIDE",
  VENDOR: "ROLE_VENDOR_CODE", //CLINICAL_STAFF 240704new role name
  DOCTOR: "ROLE_DOCTOR_CODE",
  CLINICIAN: "ROLE_CLINICIAN_CODE",
  PHARMACIST_VENDOR: "ROLE_PHARMACIST_VENDOR_CODE",
  VENDOR_ADMIN: "ROLE_VENDOR_ADMIN_CODE",
  CLIENT_POA: "ROLE_POA_CODE",
  VENDOR_CONCIERGE: "ROLE_VENDOR_CONCIERGE_CODE",
  ASSOCIATION: "ROLE_ASSOCIATION_CODE",
  NON_CLINICAL_STAFF: "ROLE_NON_CLINICAL_VENDOR",
  CAREGIVER: "ROLE_CAREGIVER_CODE",
  PREMIUM: "ROLE_PREMIUM_CODE",
  ROLE_COMMUNITY_DOCTOR: "ROLE_COMMUNITY_DOCTOR",
  COMMUNICATION_ADMINISTRATOR: "ROLE_COMMUNICATION_ADMINISTRATOR",
  COMMUNICATION_ORG_ADMIN: "ROLE_COMMUNICATION_ORG_ADMIN",
  COMMUNICATION_COMMUNITY_ADMIN: "ROLE_COMMUNICATION_COMMUNITY_ADMIN",
};

// association and vendor role
export let VENDOR_SYSTEM_ROLES = [
  SYSTEM_ROLES.ASSOCIATION,
  SYSTEM_ROLES.NON_CLINICAL_STAFF,
  SYSTEM_ROLES.VENDOR,
  // SYSTEM_ROLES.VENDOR_CONCIERGE,
  SYSTEM_ROLES.PHARMACIST_VENDOR,
  SYSTEM_ROLES.DOCTOR,
  SYSTEM_ROLES.CAREGIVER,
  SYSTEM_ROLES.PREMIUM,
];

export let ONLY_VIEW_ROLES = [SYSTEM_ROLES.ASSOCIATION, SYSTEM_ROLES.NON_CLINICAL_STAFF]; //  can only view
export let ASSOCIATION_ONLY_VIEW_ROLES = [SYSTEM_ROLES.ASSOCIATION];
export let CASE_MANAGER_ROLE = [SYSTEM_ROLES.CASE_MANAGER];
export let NON_CLINICAL_STAFF_ONLY_VIEW_ROLES = [SYSTEM_ROLES.NON_CLINICAL_STAFF];
export let ALL_VENDORS_ROLES = [
  SYSTEM_ROLES.NON_CLINICAL_STAFF,
  SYSTEM_ROLES.VENDOR,
  SYSTEM_ROLES.PHARMACIST_VENDOR,
  SYSTEM_ROLES.DOCTOR,
  SYSTEM_ROLES.CAREGIVER,
  SYSTEM_ROLES.PREMIUM,
];

export let PROFESSIONAL_SYSTEM_ROLES = [
  SYSTEM_ROLES.CASE_MANAGER,
  SYSTEM_ROLES.CARE_COORDINATOR,
  SYSTEM_ROLES.PHARMACIST,
  SYSTEM_ROLES.PHARMACY_TECHNICIAN,
  SYSTEM_ROLES.NURSE,
  SYSTEM_ROLES.PRIMARY_PHYSICIAN,
  SYSTEM_ROLES.BEHAVIORAL_HEALTH,
  SYSTEM_ROLES.COMMUNITY_MEMBERS,
  SYSTEM_ROLES.SERVICE_PROVIDER,
  SYSTEM_ROLES.ADMINISTRATOR,
  SYSTEM_ROLES.ORGANIZATION_ADMIN,
  SYSTEM_ROLES.VENDOR,
  SYSTEM_ROLES.NON_CLINICAL_STAFF,
  SYSTEM_ROLES.DOCTOR,
  SYSTEM_ROLES.CAREGIVER,
  SYSTEM_ROLES.PREMIUM,
  SYSTEM_ROLES.CLINICIAN,
  SYSTEM_ROLES.PHARMACIST_VENDOR,
  // SYSTEM_ROLES.VENDOR_ADMIN,
  SYSTEM_ROLES.NAVI_GUIDE,
  SYSTEM_ROLES.QUALITY_ASSURANCE,
  SYSTEM_ROLES.COMMUNITY_ADMINISTRATOR,
  SYSTEM_ROLES.EXTERNAL_PROVIDER,
  SYSTEM_ROLES.SUPER_ADMINISTRATOR,
  SYSTEM_ROLES.HOME_CARE_ASSISTANT,
  SYSTEM_ROLES.TELE_HEALTH_NURSE,
  SYSTEM_ROLES.VENDOR_CONCIERGE,
  SYSTEM_ROLES.ASSOCIATION,
];

export let PASSWORD_VALIDATION_TYPES = keyMirror({
  DIGIT: null,
  MIN_CHARACTERS: null,
  UPPERCASE_LETTER: null,
  LOWERCASE_LETTER: null,
  SPECIAL_CHARACTERS: null,
});

export let PASSWORD_VALIDATIONS = {
  [PASSWORD_VALIDATION_TYPES.DIGIT]: ".*[0-9]",
  [PASSWORD_VALIDATION_TYPES.SPECIAL_CHARACTERS]: ".*\\W",
  [PASSWORD_VALIDATION_TYPES.UPPERCASE_LETTER]: ".*[A-Z]",
  [PASSWORD_VALIDATION_TYPES.LOWERCASE_LETTER]: ".*[a-z]",
};

export let ALLOWED_FILE_FORMAT_MIME_TYPES = {
  [ALLOWED_FILE_FORMATS.DOC]: "application/msword",
  [ALLOWED_FILE_FORMATS.DOCX]: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  [ALLOWED_FILE_FORMATS.PDF]: "application/pdf",
  [ALLOWED_FILE_FORMATS.XLS]: "application/vnd.ms-excel",
  [ALLOWED_FILE_FORMATS.ZIP]: "application/zip",
  [ALLOWED_FILE_FORMATS.X_ZIP]: "application/x-zip",
  [ALLOWED_FILE_FORMATS.X_ZIP_COMPRESSED]: "application/x-zip-compressed",
  [ALLOWED_FILE_FORMATS.BIN]: "application/octet-stream",
  [ALLOWED_FILE_FORMATS.XLSX]: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  [ALLOWED_FILE_FORMATS.TXT]: "text/plain",
  [ALLOWED_FILE_FORMATS.XML]: "text/xml",
  [ALLOWED_FILE_FORMATS.JPG]: "image/jpeg",
  [ALLOWED_FILE_FORMATS.JPEG]: "image/jpeg",
  [ALLOWED_FILE_FORMATS.PJPG]: "image/pjpeg",
  [ALLOWED_FILE_FORMATS.PNG]: "image/png",
  [ALLOWED_FILE_FORMATS.GIF]: "image/gif",
  [ALLOWED_FILE_FORMATS.TIFF]: "image/tiff",
  [ALLOWED_FILE_FORMATS.AMR]: "audio/amr",
  [ALLOWED_FILE_FORMATS.MP3]: "audio/mp3",
  [ALLOWED_FILE_FORMATS.MPEG]: "audio/mpeg",
  [ALLOWED_FILE_FORMATS.MP4]: "video/mp4",
  [ALLOWED_FILE_FORMATS.CER]: "application/pkix-cert",
  [ALLOWED_FILE_FORMATS.CERT]: "application/x-x509-ca-cert",
  [ALLOWED_FILE_FORMATS.CRT]: "application/x-x509-user-cert",
  [ALLOWED_FILE_FORMATS.OTHER]: null,
};

export const PAGINATION = { FIRST_PAGE: 1, MAX_SIZE: 0x7fffffff };

/**
 * @name PAGES
 * */
export const PAGES = keyMirror({
  DASHBOARD: null,
  CLIENTS: null,
  EVENTS: null,
  SCHEDULING: null,
  REPORTS: null,
  ADMIN: null,
  ORGANIZATIONS: null,
  ORGANIZATION_DETAILS: null,
  COMMUNITY_DETAILS: null,
  CONTACTS: null,
  ADMIN_CLIENTS: null,
  SUGGESTED_MATCHES: null,
  MANUAL_MATCHING: null,
});

export const FEATURES = {
  CONSENT: "CONSENT",
  PERSONAL_DASHBOARD: "PERSONAL_DASHBOARD",
  TEAM_CARE: "TEAM_CARE",
  COMMUNICATIONS: "COMMUNICATIONS",
  DOCUMENTS: "DOCUMENTS",
  CLINICAL_PHARMACIST: "CLINICAL_PHARMACIST",
  NOTIFY: "NOTIFY",
  NOTIFY_FALL_DETECTION: "NOTIFY_FALL_DETECTION",
  REFERRAL_MARKETPLACE: "REFERRAL_MARKETPLACE",
  PHARMACY_MEDICATIONS: "PHARMACY_MEDICATIONS",
  NON_EMERGENCY_TRANSPORTATION: "NON_EMERGENCY_TRANSPORTATION",
  HIPAA_COMPLIANT_CALENDAR: "HIPAA_COMPLIANT_CALENDAR",
};

export const FEATURE_TITLES = {
  [FEATURES.CONSENT]: "Consent",
  [FEATURES.TEAM_CARE]: "Team Care",
  [FEATURES.PHARMACY_MEDICATIONS]: "Pharmacy/Medications",
  [FEATURES.CLINICAL_PHARMACIST]: "Clinical Pharmacist",
  [FEATURES.COMMUNICATIONS]: "Communications",
  [FEATURES.DOCUMENTS]: "Documents",
  [FEATURES.PERSONAL_DASHBOARD]: "Personal Dashboard",
  [FEATURES.NOTIFY]: "Notify",
  [FEATURES.NOTIFY_FALL_DETECTION]: "Notify Fall Detection",
  [FEATURES.REFERRAL_MARKETPLACE]: "Referral Marketplace",
  [FEATURES.NON_EMERGENCY_TRANSPORTATION]: "Transportation",
  [FEATURES.HIPAA_COMPLIANT_CALENDAR]: "Calendar",
};

export const AUTH_REQUEST_TYPES = keyMirror({
  INVITATION: null,
  RESET_PASSWORD: null,
});

export const CONTACT_STATUSES = {
  ACTIVE: "ACTIVE",
  CONFIRMED: "CONFIRMED",
  PENDING: "PENDING",
  EXPIRED: "EXPIRED",
  INACTIVE: "INACTIVE",
  DECLINED: "DECLINED",
};

export const CLIENT_STATUSES = {
  ACTIVE: "ACTIVE",
  PENDING: "PENDING",
  INACTIVE: "INACTIVE",
  DECLINED: "DECLINED",
};

export const CLIENT_SECTIONS = {
  DASHBOARD: "DASHBOARD",
  ASSESSMENTS: "ASSESSMENTS",
  SERVICE_PLANS: "SERVICE_PLANS",
  CARE_TEAM_MEMBER: "CARE_TEAM_MEMBER",
  EVENTS_AND_NOTES: "EVENTS_AND_NOTES",
  WORKFLOW: "WORKFLOW",
  CALL_HISTORY: "CALL_HISTORY",
  RIDES: "RIDES",
  EXPENSES: "EXPENSES",
  VITALS_RPM: "VITALS_RPM",
};

export const PROSPECT_SECTIONS = {
  DASHBOARD: "DASHBOARD",
  CARE_TEAM_MEMBER: "CARE_TEAM_MEMBER",
  EVENTS_AND_NOTES: "EVENTS_AND_NOTES",
  CALL_HISTORY: "CALL_HISTORY",
  RIDES: "RIDES",
};

export const TRANSPORTATION_ACTION = {
  RIDE: "RIDE",
  HISTORY: "HISTORY",
};

export const TRANSPORTATION_ACTION_DESC = {
  RIDE: "request a ride",
  HISTORY: "view ride history",
};

export const SERVICE_STATUSES = keyMirror({
  PENDING: null,
  IN_PROCESS: null,
  COMPLETED: null,
  OTHER: null,
});

export const ASSESSMENT_TYPES = {
  GAD7: "GAD7",
  PHQ9: "PHQ9",
  IN_HOME: "IN_HOME",
  HOUSING: "HOUSING_ASSESSMENT",
  CARE_MGMT: "CARE_MGMT",
  ARIZONA_SSM: "ARIZONA_SSM",
  IN_HOME_CARE: "IN_HOME_CARE",
  COMPREHENSIVE: "COMPREHENSIVE",
  NOR_CAL_COMPREHENSIVE: "NOR_CAL_COMPREHENSIVE",
  HMIS_ADULT_CHILD_INTAKE: "HMIS_ADULT_CHILD_INTAKE",
  HMIS_ADULT_CHILD_REASESSMENT: "HMIS_ADULT_CHILD_REASESSMENT",
  HMIS_ADULT_CHILD_REASESSMENT_EXIT: "HMIS_ADULT_CHILD_REASESSMENT_EXIT",
  /**
   * add new
   */
  BENEFICIARY_COLORECTAL_CANCER_SCREENING: "BENEFICIARY_COLORECTAL_CANCER_SCREENING",
  BENEFICIARY_MAMMOGRAM_SCREENING: "BENEFICIARY_MAMMOGRAM_SCREENING",
  BENEFICIARY_SCREENING_FOR_TYPE_II_DIABETES: "BENEFICIARY_SCREENING_FOR_TYPE_II_DIABETES",
  BENEFICIARY_SCREENING_DIABETIC_FOOT_AND_EYE_EXAM: "BENEFICIARY_SCREENING_DIABETIC_FOOT_AND_EYE_EXAM",

  /**
   * new workflow
   */
  FAST: "FAST",
  SHORT: "SHORT",
  REVISED: "REVISED",
  SCREEN: "SCREEN",
};

export const ASSESSMENT_STATUSES = keyMirror({
  HIDDEN: null,
  INACTIVE: null,
  COMPLETED: null,
  IN_PROCESS: null,
});

export const MEDICATION_STATUSES = keyMirror({
  ACTIVE: null,
  INACTIVE: null,
  UNKNOWN: null,
});

export const PROBLEM_STATUSES = keyMirror({
  ACTIVE: null,
  RESOLVED: null,
  OTHER: null,
});

export const GAD7_SCALE = [
  { score: "0-4", severityName: "NO", severityTitle: "No", comments: "No actions required" },
  { score: "5-9", severityName: "MILD", severityTitle: "Mild", comments: "Monitor" },
  {
    score: "10-14",
    severityName: "MODERATE",
    severityTitle: "Moderate",
    comments: "Possible clinically signification",
  },
  { score: "15-24", severityName: "SEVERE", severityTitle: "Severe", comments: "Active treatment probably warranted" },
];

/*export const PHQ9_SCALE = [
  {
    score: "0-4",
    severityName: "MINIMAL_OR_NONE",
    severityTitle: "Minimal or none",
    comments: "Monitor; may not require treatment",
  },
  {
    score: "5-9",
    severityName: "MILD",
    severityTitle: "Mild",
    comments: "Use clinical judgment (symptom duration, functional impairment) to determine necessity of treatment",
  },
  {
    score: "10-14",
    severityName: "MODERATE",
    severityTitle: "Moderate",
    comments: "Use clinical judgment (symptom duration, functional impairment) to determine necessity of treatment",
  },
  {
    score: "15-19",
    severityName: "MODERATELY_SEVERE",
    severityTitle: "Moderately severe",
    comments: "Warrants active treatment with psychotherapy, medications, or combination",
  },
  {
    score: "20-27",
    severityName: "SEVERE",
    severityTitle: "Severe",
    comments: "Warrants active treatment with psychotherapy, medications, or combination",
  },
];*/

export const ALERT_TYPES = keyMirror({
  ACTIVE_ALERT: null,
  SYSTEM_ALERT: null,
});

export const ALERT_STATUS_TYPES = keyMirror({
  CLOSED: null,
  UNTAKEN: null,
});

export const COMMUNITY_ITEMS = keyMirror({
  ADD_ZONE: null,
  ADD_HANDSET: null,
  ADD_LOCATION: null,
});

export const DASHBOARD_TYPES = keyMirror({
  CASELOAD: null,
  APPOINTMENT: null,
  RECENT_EVENT: null,
  RECENT_NOTE: null,
});

export const SERVICE_PLAN_NEED_DOMAINS = keyMirror({
  EDUCATION_TASK: null,
});

export const EVENT_GROUP_COLORS = {
  EMERGENCY: "#f09afd",
  CHANGING_HEALTH_CONDITIONS: "#fff1ca",
  MEDICATIONS_ALERTS_REACTIONS: "#d5f3b8",
  BEHAVIOR_MENTAL_HEALTH: "#bbdefb",
  GENERAL_LIFE_ASSESSMENT: "#d3dfe8",
  ABUSE_SAFETY: "#b39ddb",
  NOTES: "#ccfeea",
};

export const NOTIFICATION_RESPONSIBILITY_TYPES = {
  VIEWABLE: "V",
  CONSULTED: "C",
  ACCOUNTABLE: "A",
  RESPONSIBLE: "R",
  NOT_VIEWABLE: "N",
};

export const NOTIFICATION_CHANNELS_TYPES = keyMirror({
  SMS: null,
  FAX: null,
  EMAIL: null,
  BLUE_STONE: null,
  SECURITY_MESSAGE: null,
  PUSH_NOTIFICATION: null,
});

export const NOTE_TYPES = keyMirror({
  EVENT_NOTE: null,
  GROUP_NOTE: null,
  CLIENT_NOTE: null,
  PATIENT_NOTE: null,
});

export const REFERRAL_TYPES = keyMirror({
  INBOUND: null,
  OUTBOUND: null,
});

export const REFERRAL_ENTITY_TYPES = keyMirror({
  INQUIRY: null,
  REFERRAL: null,
});

export const REFERRAL_STATUSES = keyMirror({
  PENDING: null,
  ACCEPTED: null,
  PRE_ADMIT: null,
  DECLINED: null,
  CANCELED: null,
  DONE: null,
});

export const REFERRAL_STATUS_COLORS = {
  PENDING: "#e0e0e0",
  ACCEPTED: "#d5f3b8",
  PRE_ADMIT: "#ffedc2",
  DECLINED: "#fde1d5",
  CANCELED: "#fcccb8",
  DONE: "#d5f3b8",
};

export const LAB_RESEARCH_ORDER_STATUSES = keyMirror({
  SENT_TO_LAB: null,
  PENDING_REVIEW: null,
  REVIEWED: null,
});

export const LAB_RESEARCH_ORDER_STATUS_COLORS = {
  SENT_TO_LAB: "#e0e0e0",
  PENDING_REVIEW: "#ffedc2",
  REVIEWED: "#d5f3b8",
};

export const INCIDENT_REPORT_STATUSES = keyMirror({
  DRAFT: null,
  SUBMITTED: null,
});

export const INCIDENT_REPORT_STATUS_COLORS = {
  DRAFT: "#e0e0e0",
  SUBMITTED: "#d5f3b8",
};

export const SDOH_REPORT_STATUS_COLORS = {
  PENDING_REVIEW: "#ffedc2",
  SENT_TO_UHC: "#d5f3b8",
};

export const CLIENT_DOCUMENT_TYPES = keyMirror({
  CCD: null,
  FACESHEET: null,
  CUSTOM: null,
  FAX: null,
});

export const CLIENT_DOCUMENT_TYPE_TITLES = {
  [CLIENT_DOCUMENT_TYPES.CCD]: "CCD",
  [CLIENT_DOCUMENT_TYPES.FACESHEET]: "Facesheet",
  [CLIENT_DOCUMENT_TYPES.CUSTOM]: "Custom",
  [CLIENT_DOCUMENT_TYPES.FAX]: "Fax",
};

export const SERVICE_PLAN_DOMAINS = keyMirror({
  HOUSING: null,
});

export const MEDICATION_STATUS_COLORS = {
  INACTIVE: "#e0e0e0",
  ACTIVE: "#d5f3b8",
  COMPLETED: "#e0e0e0",
  ENTERED_IN_ERROR: "#fcccb8",
};

export const CARE_TEAM_AFFILIATION_TYPES = {
  REGULAR: "REGULAR",
  AFFILIATED: "AFFILIATED",
  BOTH: "BOTH",
};

export const SERVICE_MESSAGE_TYPES = {
  CALL_END: "CALL_END",
  INITIATE_CALL: "INITIATE_CALL",
  CALL_ACCEPTED: "CALL_ACCEPTED",
  CALL_MEMBER_DECLINED: "CALL_MEMBER_DECLINED",
  CALL_MEMBERS_TIMEOUT: "CALL_MEMBERS_TIMEOUT",
  CALL_PENDING_MEMBERS: "CALL_PENDING_MEMBERS",
  CALL_MEMBER_JOINED: "CALL_MEMBER_JOINED",
  CALL_MEMBER_LEFT: "CALL_MEMBER_LEFT",
  CALL_CONVERSATION_UPDATED: "CALL_CONVERSATION_UPDATED",
};

export const E_SIGN_REQUEST_RECIPIENT_TYPES = {
  SELF: "SELF",
  CLIENT: "CLIENT",
  STAFF: "STAFF",
};

export const E_SIGN_REQUEST_NOTIFICATION_METHODS = {
  SMS: "SMS",
  CHAT: "CHAT",
  EMAIL: "EMAIL",
  SIGN_NOW: "SIGN_NOW",
};

export const E_SIGN_STATUSES = {
  SENT: "SENT",
  SIGNED: "SIGNED",
  FAILED: "FAILED",
  RECEIVED: "RECEIVED",
  REQUESTED: "REQUESTED",
  REQUEST_EXPIRED: "REQUEST_EXPIRED",
  REQUEST_CANCELED: "REQUEST_CANCELED",
};

export const E_SIGN_ACTIONS = {
  DOCUMENT_SENT: "DOCUMENT_SENT",
  DOCUMENT_SIGNED: "DOCUMENT_SIGNED",
  DOCUMENT_RECEIVED: "DOCUMENT_RECEIVED",
  DOCUMENT_SIGNATURE_FAILED: "DOCUMENT_SIGNATURE_FAILED",
  DOCUMENT_SIGNATURE_REQUESTED: "DOCUMENT_SIGNATURE_REQUESTED",
  DOCUMENT_SIGNATURE_REQUEST_EXPIRED: "DOCUMENT_SIGNATURE_REQUEST_EXPIRED",
  DOCUMENT_SIGNATURE_REQUEST_CANCELED: "DOCUMENT_SIGNATURE_REQUEST_CANCELED",
};

export const E_SIGN_BULK_STATUSES = {
  //Signature created, but not submitted to PDCFlow yet
  CREATED: "CREATED",
  //Signature request sent to PDCFLow
  SIGNATURE_REQUESTED: "SIGNATURE_REQUESTED",
  //Signature request sent to PDCFLow for document without signature areas
  REVIEW_REQUESTED: "REVIEW_REQUESTED",
  //Failed to submit signature request to PDCFlow
  REQUEST_FAILED: "REQUEST_FAILED",
  //Signature received
  SIGNED: "SIGNED",
  //Document without signature accepted on PDCFlow
  REVIEWED: "REVIEWED",
  //Received error from PDCFlow
  SIGNATURE_FAILED: "SIGNATURE_FAILED",
  //Signature expired
  EXPIRED: "EXPIRED",
  //Signature canceled
  CANCELED: "CANCELED",
};

export const E_SIGN_REQUEST_STEPS = {
  SIGNATURE_REQUEST: "SIGNATURE_REQUEST",
  DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
  DOCUMENT_TEMPLATE_PREVIEW: "DOCUMENT_TEMPLATE_PREVIEW",
  DOCUMENT_TEMPLATE_PREVIEW_MULTIPLE: "DOCUMENT_TEMPLATE_PREVIEW_MULTIPLE",
  MULTIPLE_SIGNATURE_REQUEST: "MULTIPLE_SIGNATURE_REQUEST",
};

export const E_SIGN_DOCUMENT_TEMPLATE_BUILDER_STEPS = {
  FILE_UPLOAD: "FILE_UPLOAD",
  ORGANIZATION: "ORGANIZATION",
  FIELD_EDITOR: "FIELD_EDITOR",
};

export const E_SIGN_DOCUMENT_TEMPLATE_STATUSES = {
  DRAFT: "DRAFT",
  COMPLETED: "COMPLETED",
};

export const APPOINTMENT_TYPES = {
  CHECK_UP: "CHECK_UP",
  EMERGENCY: "EMERGENCY",
  FOLLOW_UP: "FOLLOW_UP",
  POST_OP: "POST_OP",
  ROUTINE: "ROUTINE",
  OTHER: "OTHER",
};

export const APPOINTMENT_TYPE_COLOR_SCHEME = {
  [APPOINTMENT_TYPES.CHECK_UP]: { name: "yellow", value: "#fff2d0" },
  [APPOINTMENT_TYPES.EMERGENCY]: { name: "red", value: "#ffc9bc" },
  [APPOINTMENT_TYPES.FOLLOW_UP]: { name: "blue", value: "#daedf5" },
  [APPOINTMENT_TYPES.POST_OP]: { name: "dark-blue", value: "#c6ccf4" },
  [APPOINTMENT_TYPES.ROUTINE]: { name: "green", value: "#acebc6" },
  [APPOINTMENT_TYPES.OTHER]: { name: "green", value: "#efbee6" },
};

export const APPOINTMENT_STATUSES = {
  PLANNED: "PLANNED",
  RESCHEDULED: "RESCHEDULED",
  TRIAGED: "TRIAGED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  ENTERED_IN_ERROR: "ENTERED_IN_ERROR",
};

/**
 * @name ACTION_TYPES
 * */
export const ACTION_TYPES = keyMirror({
  CLEAR_ERROR: null,
  CHANGE_ERROR: null,

  CLEAR_ALL_AUTH_DATA: null,

  VALIDATE_TOKEN_REQUEST: null,
  VALIDATE_TOKEN_SUCCESS: null,
  VALIDATE_TOKEN_FAILURE: null,

  REMOVE_TOKEN_REQUEST: null,
  REMOVE_TOKEN_SUCCESS: null,

  CLEAR_LOGIN_FORM: null,
  CLEAR_LOGIN_FORM_ERROR: null,
  CLEAR_LOGIN_FORM_FIELD_ERROR: null,
  CHANGE_LOGIN_FORM_FIELD: null,
  CHANGE_LOGIN_FORM_FIELDS: null,
  VALIDATE_LOGIN_FORM: null,

  CLEAR_RESET_PASSWORD_FORM: null,
  CLEAR_RESET_PASSWORD_FORM_ERROR: null,
  CLEAR_RESET_PASSWORD_FORM_FIELD_ERROR: null,
  CHANGE_RESET_PASSWORD_FORM_FIELD: null,
  CHANGE_RESET_PASSWORD_FORM_FIELDS: null,
  VALIDATE_RESET_PASSWORD_FORM: null,
  RESET_PASSWORD_REQUEST: null,
  RESET_PASSWORD_SUCCESS: null,
  RESET_PASSWORD_FAILURE: null,

  CLEAR_NEW_PASSWORD_FORM: null,
  CLEAR_NEW_PASSWORD_FORM_ERROR: null,
  CLEAR_NEW_PASSWORD_FORM_FIELD_ERROR: null,
  CHANGE_NEW_PASSWORD_FORM_FIELD: null,
  CHANGE_NEW_PASSWORD_FORM_FIELDS: null,
  VALIDATE_NEW_PASSWORD_FORM: null,
  NEW_PASSWORD_REQUEST: null,
  NEW_PASSWORD_SUCCESS: null,
  NEW_PASSWORD_FAILURE: null,

  CLEAR_OLD_PASSWORD_FORM: null,
  CLEAR_OLD_PASSWORD_FORM_ERROR: null,
  CLEAR_OLD_PASSWORD_FORM_FIELD_ERROR: null,
  CHANGE_OLD_PASSWORD_FORM_FIELD: null,
  CHANGE_OLD_PASSWORD_FORM_FIELDS: null,
  VALIDATE_OLD_PASSWORD_FORM: null,
  OLD_PASSWORD_REQUEST: null,
  OLD_PASSWORD_SUCCESS: null,
  OLD_PASSWORD_FAILURE: null,

  CLEAR_CREATE_PASSWORD_FORM: null,
  CLEAR_CREATE_PASSWORD_FORM_ERROR: null,
  CLEAR_CREATE_PASSWORD_FORM_FIELD_ERROR: null,
  CHANGE_CREATE_PASSWORD_FORM_FIELD: null,
  CHANGE_CREATE_PASSWORD_FORM_FIELDS: null,
  VALIDATE_CREATE_PASSWORD_FORM: null,
  CREATE_PASSWORD_REQUEST: null,
  CREATE_PASSWORD_SUCCESS: null,
  CREATE_PASSWORD_FAILURE: null,
  DECLINE_INVITATION_REQUEST: null,
  DECLINE_INVITATION_SUCCESS: null,
  DECLINE_INVITATION_FAILURE: null,

  CLEAR_COMPLEXITY_RULES: null,
  CLEAR_COMPLEXITY_RULES_ERROR: null,
  LOAD_COMPLEXITY_RULES_REQUEST: null,
  LOAD_COMPLEXITY_RULES_SUCCESS: null,
  LOAD_COMPLEXITY_RULES_FAILURE: null,

  CLEAR_VALIDATE_PASSWORD_TOKEN: null,
  CLEAR_VALIDATE_PASSWORD_TOKEN_ERROR: null,
  LOAD_VALIDATE_PASSWORD_TOKEN_REQUEST: null,
  LOAD_VALIDATE_PASSWORD_TOKEN_SUCCESS: null,
  LOAD_VALIDATE_PASSWORD_TOKEN_FAILURE: null,

  SIGN_IN_SUCCESS: null,
  SIGN_IN_FAILURE: null,

  LOGIN_REQUEST: null,
  LOGIN_SUCCESS: null,
  LOGIN_FAILURE: null,
  CLEAR_LOGIN_ERROR: null,
  RESTORE_LOGGED_IN_USER: null,

  UPDATE_AUTH_USER_REQUEST: null,
  UPDATE_AUTH_USER_SUCCESS: null,
  UPDATE_AUTH_USER_FAILURE: null,
  CLEAR_UPDATE_AUTH_USER_ERROR: null,

  LOGIN_4D_REQUEST: null,
  LOGIN_4D_SUCCESS: null,
  LOGIN_4D_FAILURE: null,

  CLEAR_SESSION_ERROR: null,
  SET_SESSION_ABOUT_TO_EXPIRE: null,

  LOGOUT_REQUEST: null,
  LOGOUT_SUCCESS: null,
  LOGOUT_FAILURE: null,
  CLEAR_LOGOUT_ERROR: null,

  CLEAR_ACTIVE_ALERT_LIST_ERROR: null,

  CLEAR_ACTIVE_ALERT_LIST: null,

  LOAD_ACTIVE_ALERT_LIST_REQUEST: null,
  LOAD_ACTIVE_ALERT_LIST_SUCCESS: null,
  LOAD_ACTIVE_ALERT_LIST_FAILURE: null,

  CLEAR_ACTIVE_ALERT_HISTORY_ERROR: null,

  CLEAR_ACTIVE_ALERT_HISTORY: null,

  LOAD_ACTIVE_ALERT_HISTORY_REQUEST: null,
  LOAD_ACTIVE_ALERT_HISTORY_SUCCESS: null,
  LOAD_ACTIVE_ALERT_HISTORY_FAILURE: null,

  CLEAR_ACTIVE_ALERT_COUNT: null,
  CLEAR_ACTIVE_ALERT_COUNT_ERROR: null,
  LOAD_ACTIVE_ALERT_COUNT_REQUEST: null,
  LOAD_ACTIVE_ALERT_COUNT_SUCCESS: null,
  LOAD_ACTIVE_ALERT_COUNT_FAILURE: null,

  CLEAR_SYSTEM_ALERT_LIST_ERROR: null,
  CLEAR_SYSTEM_ALERT_LIST: null,
  LOAD_SYSTEM_ALERT_LIST_REQUEST: null,
  LOAD_SYSTEM_ALERT_LIST_SUCCESS: null,
  LOAD_SYSTEM_ALERT_LIST_FAILURE: null,

  CLEAR_SYSTEM_ALERT_HISTORY_ERROR: null,

  CLEAR_SYSTEM_ALERT_HISTORY: null,

  LOAD_SYSTEM_ALERT_HISTORY_REQUEST: null,
  LOAD_SYSTEM_ALERT_HISTORY_SUCCESS: null,
  LOAD_SYSTEM_ALERT_HISTORY_FAILURE: null,

  CLEAR_SYSTEM_ALERT_COUNT: null,
  CLEAR_SYSTEM_ALERT_COUNT_ERROR: null,
  LOAD_SYSTEM_ALERT_COUNT_REQUEST: null,
  LOAD_SYSTEM_ALERT_COUNT_SUCCESS: null,
  LOAD_SYSTEM_ALERT_COUNT_FAILURE: null,

  CLEAR_ORGANIZATION_LIST_ERROR: null,
  CLEAR_ORGANIZATION_LIST: null,
  CLEAR_ORGANIZATION_LIST_FILTER: null,
  CHANGE_ORGANIZATION_LIST_FILTER_FIELD: null,
  CHANGE_ORGANIZATION_LIST_SORTING: null,
  LOAD_ORGANIZATION_LIST_REQUEST: null,
  LOAD_ORGANIZATION_LIST_SUCCESS: null,
  LOAD_ORGANIZATION_LIST_FAILURE: null,

  CLEAR_ORGANIZATION_HISTORY_ERROR: null,

  CLEAR_ORGANIZATION_HISTORY: null,

  LOAD_ORGANIZATION_HISTORY_REQUEST: null,
  LOAD_ORGANIZATION_HISTORY_SUCCESS: null,
  LOAD_ORGANIZATION_HISTORY_FAILURE: null,

  CLEAR_ORGANIZATION_COUNT: null,
  CLEAR_ORGANIZATION_COUNT_ERROR: null,
  LOAD_ORGANIZATION_COUNT_REQUEST: null,
  LOAD_ORGANIZATION_COUNT_SUCCESS: null,
  LOAD_ORGANIZATION_COUNT_FAILURE: null,

  CLEAR_CAN_ADD_ORGANIZATION: null,
  CLEAR_CAN_ADD_ORGANIZATION_ERROR: null,
  LOAD_CAN_ADD_ORGANIZATION_REQUEST: null,
  LOAD_CAN_ADD_ORGANIZATION_SUCCESS: null,
  LOAD_CAN_ADD_ORGANIZATION_FAILURE: null,

  CLEAR_ORGANIZATION_DETAILS: null,
  CLEAR_ORGANIZATION_DETAILS_ERROR: null,
  LOAD_ORGANIZATION_DETAILS_REQUEST: null,
  LOAD_ORGANIZATION_DETAILS_SUCCESS: null,
  LOAD_ORGANIZATION_DETAILS_FAILURE: null,

  DOWNLOAD_ORGANIZATION_LOGO_REQUEST: null,
  DOWNLOAD_ORGANIZATION_LOGO_SUCCESS: null,
  DOWNLOAD_ORGANIZATION_LOGO_FAILURE: null,

  LOAD_ORGANIZATION_FORM_DATA_REQUEST: null,
  LOAD_ORGANIZATION_FORM_DATA_SUCCESS: null,
  LOAD_ORGANIZATION_FORM_DATA_FAILURE: null,

  CLEAR_ORGANIZATION_FORM: null,
  CLEAR_ORGANIZATION_FORM_ERROR: null,
  CLEAR_ORGANIZATION_FORM_FIELD_ERROR: null,

  VALIDATE_ORGANIZATION_DATA_UNIQ_REQUEST: null,
  VALIDATE_ORGANIZATION_DATA_UNIQ_SUCCESS: null,
  VALIDATE_ORGANIZATION_DATA_UNIQ_FAILURE: null,

  CHANGE_ORGANIZATION_FORM_TAB: null,

  CHANGE_ORGANIZATION_FORM_FIELD: null,
  CHANGE_ORGANIZATION_FORM_FIELDS: null,
  CHANGE_ORGANIZATION_FORM_MARKET_PLACE_FIELD: null,

  VALIDATE_ORGANIZATION_FORM: null,
  VALIDATE_ORGANIZATION_FORM_LEGAL_INFO: null,
  VALIDATE_ORGANIZATION_FORM_MARKETPLACE: null,
  VALIDATE_ORGANIZATION_FORM_AFFILIATE_RELATIONSHIP: null,

  SAVE_ORGANIZATION_REQUEST: null,
  SAVE_ORGANIZATION_SUCCESS: null,
  SAVE_ORGANIZATION_FAILURE: null,

  CLEAR_COMMUNITY_LIST_ERROR: null,
  CLEAR_COMMUNITY_LIST: null,
  CLEAR_COMMUNITY_LIST_FILTER: null,
  CHANGE_COMMUNITY_LIST_FILTER: null,
  CHANGE_COMMUNITY_LIST_SORTING: null,
  LOAD_COMMUNITY_LIST_REQUEST: null,
  LOAD_COMMUNITY_LIST_SUCCESS: null,
  LOAD_COMMUNITY_LIST_FAILURE: null,

  CLEAR_CAN_VIEW_MARKETPLACE: null,
  CLEAR_CAN_VIEW_MARKETPLACE_ERROR: null,
  LOAD_CAN_VIEW_MARKETPLACE_REQUEST: null,
  LOAD_CAN_VIEW_MARKETPLACE_SUCCESS: null,
  LOAD_CAN_VIEW_MARKETPLACE_FAILURE: null,

  CLEAR_COMMUNITY_HISTORY_ERROR: null,

  CLEAR_COMMUNITY_HISTORY: null,

  LOAD_COMMUNITY_HISTORY_REQUEST: null,
  LOAD_COMMUNITY_HISTORY_SUCCESS: null,
  LOAD_COMMUNITY_HISTORY_FAILURE: null,

  CLEAR_COMMUNITY_COUNT: null,
  CLEAR_COMMUNITY_COUNT_ERROR: null,
  LOAD_COMMUNITY_COUNT_REQUEST: null,
  LOAD_COMMUNITY_COUNT_SUCCESS: null,
  LOAD_COMMUNITY_COUNT_FAILURE: null,

  CLEAR_CAN_ADD_COMMUNITY: null,
  CLEAR_CAN_ADD_COMMUNITY_ERROR: null,
  LOAD_CAN_ADD_COMMUNITY_REQUEST: null,
  LOAD_CAN_ADD_COMMUNITY_SUCCESS: null,
  LOAD_CAN_ADD_COMMUNITY_FAILURE: null,

  CLEAR_COMMUNITY_DETAILS: null,
  CLEAR_COMMUNITY_DETAILS_ERROR: null,
  LOAD_COMMUNITY_DETAILS_REQUEST: null,
  LOAD_COMMUNITY_DETAILS_SUCCESS: null,
  LOAD_COMMUNITY_DETAILS_FAILURE: null,

  DOWNLOAD_COMMUNITY_LOGO_REQUEST: null,
  DOWNLOAD_COMMUNITY_LOGO_SUCCESS: null,
  DOWNLOAD_COMMUNITY_LOGO_FAILURE: null,

  CLEAR_COMMUNITY_FORM: null,
  CLEAR_COMMUNITY_FORM_ERROR: null,
  CLEAR_CERTIFICATE_FORM_FIELDS: null,
  CLEAR_COMMUNITY_FORM_FIELD_ERROR: null,

  CHANGE_COMMUNITY_FORM_TAB: null,

  CHANGE_COMMUNITY_FORM_FIELD: null,
  CHANGE_COMMUNITY_FORM_FIELDS: null,
  CHANGE_COMMUNITY_FORM_FIELD_DEEP: null,
  CHANGE_COMMUNITY_FORM_MARKET_PLACE_FIELD: null,
  ADD_COMMUNITY_FORM_REFERRAL_EMAILS_FIELD: null,
  SET_INITIAL_COMMUNITY_FORM_REFERRAL_EMAILS_FIELD: null,

  VALIDATE_COMMUNITY_FORM: null,
  VALIDATE_COMMUNITY_FORM_LEGAL_INFO: null,
  VALIDATE_COMMUNITY_FORM_MARKETPLACE: null,
  VALIDATE_COMMUNITY_FORM_AFFILIATE_RELATIONSHIP: null,

  VALIDATE_COMMUNITY_DATA_UNIQ_REQUEST: null,
  VALIDATE_COMMUNITY_DATA_UNIQ_SUCCESS: null,
  VALIDATE_COMMUNITY_DATA_UNIQ_FAILURE: null,

  SAVE_COMMUNITY_REQUEST: null,
  SAVE_COMMUNITY_SUCCESS: null,
  SAVE_COMMUNITY_FAILURE: null,

  LOAD_SERVER_SELF_TRUSTED_CERT_REQUEST: null,
  LOAD_SERVER_SELF_TRUSTED_CERT_SUCCESS: null,
  LOAD_SERVER_SELF_TRUSTED_CERT_FAILURE: null,

  VALIDATE_COMMUNITY_BUSINESS_UNIT_CODES_REQUEST: null,
  VALIDATE_COMMUNITY_BUSINESS_UNIT_CODES_SUCCESS: null,
  VALIDATE_COMMUNITY_BUSINESS_UNIT_CODES_FAILURE: null,

  CLEAR_COMMUNITY_HANDSET_LIST_ERROR: null,

  CLEAR_COMMUNITY_HANDSET_LIST: null,
  CLEAR_COMMUNITY_HANDSET_LIST_FILTER: null,
  CHANGE_COMMUNITY_HANDSET_LIST_FILTER: null,
  LOAD_COMMUNITY_HANDSET_LIST_REQUEST: null,
  LOAD_COMMUNITY_HANDSET_LIST_SUCCESS: null,
  LOAD_COMMUNITY_HANDSET_LIST_FAILURE: null,

  CLEAR_COMMUNITY_HANDSET_HISTORY_ERROR: null,

  CLEAR_COMMUNITY_HANDSET_HISTORY: null,
  LOAD_COMMUNITY_HANDSET_HISTORY_REQUEST: null,
  LOAD_COMMUNITY_HANDSET_HISTORY_SUCCESS: null,
  LOAD_COMMUNITY_HANDSET_HISTORY_FAILURE: null,

  CLEAR_COMMUNITY_HANDSET_COUNT: null,
  CLEAR_COMMUNITY_HANDSET_COUNT_ERROR: null,
  LOAD_COMMUNITY_HANDSET_COUNT_REQUEST: null,
  LOAD_COMMUNITY_HANDSET_COUNT_SUCCESS: null,
  LOAD_COMMUNITY_HANDSET_COUNT_FAILURE: null,

  CLEAR_COMMUNITY_HANDSET_FORM: null,
  CLEAR_COMMUNITY_HANDSET_FORM_ERROR: null,
  CLEAR_COMMUNITY_HANDSET_FORM_FIELD_ERROR: null,

  CHANGE_COMMUNITY_HANDSET_FORM_TAB: null,

  CHANGE_COMMUNITY_HANDSET_FORM_FIELD: null,
  CHANGE_COMMUNITY_HANDSET_FORM_FIELDS: null,

  VALIDATE_COMMUNITY_HANDSET_FORM: null,
  SAVE_COMMUNITY_HANDSET_REQUEST: null,
  SAVE_COMMUNITY_HANDSET_SUCCESS: null,
  SAVE_COMMUNITY_HANDSET_FAILURE: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_LIST_ERROR: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_LIST: null,
  CLEAR_COMMUNITY_DEVICE_TYPE_LIST_FILTER: null,
  CHANGE_COMMUNITY_DEVICE_TYPE_LIST_FILTER: null,
  LOAD_COMMUNITY_DEVICE_TYPE_LIST_REQUEST: null,
  LOAD_COMMUNITY_DEVICE_TYPE_LIST_SUCCESS: null,
  LOAD_COMMUNITY_DEVICE_TYPE_LIST_FAILURE: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_HISTORY_ERROR: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_HISTORY: null,
  LOAD_COMMUNITY_DEVICE_TYPE_HISTORY_REQUEST: null,
  LOAD_COMMUNITY_DEVICE_TYPE_HISTORY_SUCCESS: null,
  LOAD_COMMUNITY_DEVICE_TYPE_HISTORY_FAILURE: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_DETAILS: null,
  CLEAR_COMMUNITY_DEVICE_TYPE_DETAILS_ERROR: null,
  LOAD_COMMUNITY_DEVICE_TYPE_DETAILS_REQUEST: null,
  LOAD_COMMUNITY_DEVICE_TYPE_DETAILS_SUCCESS: null,
  LOAD_COMMUNITY_DEVICE_TYPE_DETAILS_FAILURE: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_COUNT: null,
  CLEAR_COMMUNITY_DEVICE_TYPE_COUNT_ERROR: null,
  LOAD_COMMUNITY_DEVICE_TYPE_COUNT_REQUEST: null,
  LOAD_COMMUNITY_DEVICE_TYPE_COUNT_SUCCESS: null,
  LOAD_COMMUNITY_DEVICE_TYPE_COUNT_FAILURE: null,

  CLEAR_COMMUNITY_DEVICE_TYPE_FORM: null,
  CLEAR_COMMUNITY_DEVICE_TYPE_FORM_ERROR: null,
  CLEAR_COMMUNITY_DEVICE_TYPE_FORM_FIELD_ERROR: null,

  CHANGE_COMMUNITY_DEVICE_TYPE_FORM_TAB: null,

  CHANGE_COMMUNITY_DEVICE_TYPE_FORM_FIELD: null,
  CHANGE_COMMUNITY_DEVICE_TYPE_FORM_FIELDS: null,

  VALIDATE_COMMUNITY_DEVICE_TYPE_FORM: null,
  SAVE_COMMUNITY_DEVICE_TYPE_REQUEST: null,
  SAVE_COMMUNITY_DEVICE_TYPE_SUCCESS: null,
  SAVE_COMMUNITY_DEVICE_TYPE_FAILURE: null,

  CLEAR_COMMUNITY_LOCATION_LIST_ERROR: null,

  CLEAR_COMMUNITY_LOCATION_LIST: null,
  CLEAR_COMMUNITY_LOCATION_LIST_FILTER: null,
  CHANGE_COMMUNITY_LOCATION_LIST_FILTER: null,
  LOAD_COMMUNITY_LOCATION_LIST_REQUEST: null,
  LOAD_COMMUNITY_LOCATION_LIST_SUCCESS: null,
  LOAD_COMMUNITY_LOCATION_LIST_FAILURE: null,

  CLEAR_COMMUNITY_LOCATION_HISTORY_ERROR: null,

  CLEAR_COMMUNITY_LOCATION_HISTORY: null,
  LOAD_COMMUNITY_LOCATION_HISTORY_REQUEST: null,
  LOAD_COMMUNITY_LOCATION_HISTORY_SUCCESS: null,
  LOAD_COMMUNITY_LOCATION_HISTORY_FAILURE: null,

  CLEAR_COMMUNITY_LOCATION_COUNT: null,
  CLEAR_COMMUNITY_LOCATION_COUNT_ERROR: null,
  LOAD_COMMUNITY_LOCATION_COUNT_REQUEST: null,
  LOAD_COMMUNITY_LOCATION_COUNT_SUCCESS: null,
  LOAD_COMMUNITY_LOCATION_COUNT_FAILURE: null,

  CLEAR_COMMUNITY_LOCATION_FORM: null,
  CLEAR_COMMUNITY_LOCATION_FORM_ERROR: null,
  CLEAR_COMMUNITY_LOCATION_FORM_FIELD_ERROR: null,

  CHANGE_COMMUNITY_LOCATION_FORM_TAB: null,

  CHANGE_COMMUNITY_LOCATION_FORM_FIELD: null,
  CHANGE_COMMUNITY_LOCATION_FORM_FIELDS: null,

  VALIDATE_COMMUNITY_LOCATION_FORM: null,
  SAVE_COMMUNITY_LOCATION_REQUEST: null,
  SAVE_COMMUNITY_LOCATION_SUCCESS: null,
  SAVE_COMMUNITY_LOCATION_FAILURE: null,

  CLEAR_COMMUNITY_ZONE_LIST_ERROR: null,

  CLEAR_COMMUNITY_ZONE_LIST: null,
  CLEAR_COMMUNITY_ZONE_LIST_FILTER: null,
  CHANGE_COMMUNITY_ZONE_LIST_FILTER: null,

  LOAD_COMMUNITY_ZONE_LIST_REQUEST: null,
  LOAD_COMMUNITY_ZONE_LIST_SUCCESS: null,
  LOAD_COMMUNITY_ZONE_LIST_FAILURE: null,

  CLEAR_COMMUNITY_ZONE_HISTORY_ERROR: null,

  CLEAR_COMMUNITY_ZONE_HISTORY: null,

  LOAD_COMMUNITY_ZONE_HISTORY_REQUEST: null,
  LOAD_COMMUNITY_ZONE_HISTORY_SUCCESS: null,
  LOAD_COMMUNITY_ZONE_HISTORY_FAILURE: null,

  CLEAR_COMMUNITY_ZONE_COUNT: null,
  CLEAR_COMMUNITY_ZONE_COUNT_ERROR: null,
  LOAD_COMMUNITY_ZONE_COUNT_REQUEST: null,
  LOAD_COMMUNITY_ZONE_COUNT_SUCCESS: null,
  LOAD_COMMUNITY_ZONE_COUNT_FAILURE: null,

  CLEAR_COMMUNITY_ZONE_FORM: null,
  CLEAR_COMMUNITY_ZONE_FORM_ERROR: null,
  CLEAR_COMMUNITY_ZONE_FORM_FIELD_ERROR: null,

  CHANGE_COMMUNITY_ZONE_FORM_TAB: null,

  CHANGE_COMMUNITY_ZONE_FORM_FIELD: null,
  CHANGE_COMMUNITY_ZONE_FORM_FIELDS: null,

  VALIDATE_COMMUNITY_ZONE_FORM: null,
  SAVE_COMMUNITY_ZONE_REQUEST: null,
  SAVE_COMMUNITY_ZONE_SUCCESS: null,
  SAVE_COMMUNITY_ZONE_FAILURE: null,

  UPDATE_SIDE_BAR: null,

  CLEAR_STATE_LIST: null,
  CLEAR_STATE_LIST_ERROR: null,
  LOAD_STATE_LIST_REQUEST: null,
  LOAD_STATE_LIST_SUCCESS: null,
  LOAD_STATE_LIST_FAILURE: null,

  CLEAR_GENDER_LIST: null,
  LOAD_GENDER_LIST_SUCCESS: null,
  LOAD_GENDER_LIST_FAILURE: null,

  CLEAR_ETHNICITY_LIST: null,
  LOAD_ETHNICITY_LIST_SUCCESS: null,
  LOAD_ETHNICITY_LIST_FAILURE: null,

  CLEAR_MARITAL_STATUS_LIST: null,
  LOAD_MARITAL_STATUS_LIST_SUCCESS: null,
  LOAD_MARITAL_STATUS_LIST_FAILURE: null,

  CLEAR_AGE_GROUP_LIST: null,
  LOAD_AGE_GROUP_LIST_SUCCESS: null,
  LOAD_AGE_GROUP_LIST_FAILURE: null,

  CLEAR_PRIMARY_FOCUS_LIST: null,
  LOAD_PRIMARY_FOCUS_LIST_REQUEST: null,
  LOAD_PRIMARY_FOCUS_LIST_SUCCESS: null,
  LOAD_PRIMARY_FOCUS_LIST_FAILURE: null,

  CLEAR_CARE_LEVEL_LIST: null,
  LOAD_CARE_LEVEL_LIST_SUCCESS: null,
  LOAD_CARE_LEVEL_LIST_FAILURE: null,

  CLEAR_NOTE_TYPE_LIST: null,
  CLEAR_NOTE_TYPE_LIST_ERROR: null,
  LOAD_NOTE_TYPE_LIST_REQUEST: null,
  LOAD_NOTE_TYPE_LIST_SUCCESS: null,
  LOAD_NOTE_TYPE_LIST_FAILURE: null,

  CLEAR_NOTE_ENCOUNTER_TYPE_LIST: null,
  CLEAR_NOTE_ENCOUNTER_TYPE_LIST_ERROR: null,
  LOAD_NOTE_ENCOUNTER_TYPE_LIST_REQUEST: null,
  LOAD_NOTE_ENCOUNTER_TYPE_LIST_SUCCESS: null,
  LOAD_NOTE_ENCOUNTER_TYPE_LIST_FAILURE: null,

  CLEAR_NOTE_ADMITTANCE_LIST: null,
  LOAD_NOTE_ADMITTANCE_LIST_SUCCESS: null,
  LOAD_NOTE_ADMITTANCE_LIST_FAILURE: null,

  CLEAR_EVENT_TYPE_LIST: null,
  CLEAR_EVENT_TYPE_LIST_ERROR: null,
  LOAD_EVENT_TYPE_LIST_REQUEST: null,
  LOAD_EVENT_TYPE_LIST_SUCCESS: null,
  LOAD_EVENT_TYPE_LIST_FAILURE: null,

  CLEAR_COMMUNITY_TYPE_LIST: null,
  LOAD_COMMUNITY_TYPE_LIST_REQUEST: null,
  LOAD_COMMUNITY_TYPE_LIST_SUCCESS: null,
  LOAD_COMMUNITY_TYPE_LIST_FAILURE: null,

  CLEAR_LANGUAGE_SERVICE_LIST: null,
  LOAD_LANGUAGE_SERVICE_LIST_SUCCESS: null,
  LOAD_LANGUAGE_SERVICE_LIST_FAILURE: null,

  CLEAR_ADDITIONAL_SERVICE_LIST: null,
  LOAD_ADDITIONAL_SERVICE_LIST_SUCCESS: null,
  LOAD_ADDITIONAL_SERVICE_LIST_FAILURE: null,

  CLEAR_TREATMENT_SERVICE_LIST: null,
  LOAD_TREATMENT_SERVICE_LIST_REQUEST: null,
  LOAD_TREATMENT_SERVICE_LIST_SUCCESS: null,
  LOAD_TREATMENT_SERVICE_LIST_FAILURE: null,

  CLEAR_EMERGENCY_SERVICE_LIST: null,
  LOAD_EMERGENCY_SERVICE_LIST_SUCCESS: null,
  LOAD_EMERGENCY_SERVICE_LIST_FAILURE: null,

  CLEAR_DOMAIN_LIST: null,
  LOAD_DOMAIN_LIST_REQUEST: null,
  LOAD_DOMAIN_LIST_SUCCESS: null,
  LOAD_DOMAIN_LIST_FAILURE: null,

  CLEAR_PROGRAM_TYPE_LIST: null,
  LOAD_PROGRAM_TYPE_LIST_REQUEST: null,
  LOAD_PROGRAM_TYPE_LIST_SUCCESS: null,
  LOAD_PROGRAM_TYPE_LIST_FAILURE: null,

  CLEAR_PROGRAM_SUBTYPE_LIST: null,
  LOAD_PROGRAM_SUBTYPE_LIST_REQUEST: null,
  LOAD_PROGRAM_SUBTYPE_LIST_SUCCESS: null,
  LOAD_PROGRAM_SUBTYPE_LIST_FAILURE: null,

  CLEAR_PRIORITY_LIST: null,
  LOAD_PRIORITY_LIST_REQUEST: null,
  LOAD_PRIORITY_LIST_SUCCESS: null,
  LOAD_PRIORITY_LIST_FAILURE: null,

  CLEAR_ORGANIZATION_TYPE_LIST: null,
  LOAD_ORGANIZATION_TYPE_LIST_SUCCESS: null,
  LOAD_ORGANIZATION_TYPE_LIST_FAILURE: null,

  CLEAR_SYSTEM_ROLE_LIST: null,
  CLEAR_SYSTEM_ROLE_LIST_ERROR: null,
  LOAD_SYSTEM_ROLE_LIST_REQUEST: null,
  LOAD_SYSTEM_ROLE_LIST_SUCCESS: null,
  LOAD_SYSTEM_ROLE_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_EMPLOYEE_LIST: null,
  LOAD_CARE_TEAM_EMPLOYEE_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_EMPLOYEE_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_ROLE_LIST: null,
  LOAD_CARE_TEAM_ROLE_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_ROLE_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_CHANNEL_LIST: null,
  LOAD_CARE_TEAM_CHANNEL_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_CHANNEL_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_RESPONSIBILITY_LIST: null,
  LOAD_CARE_TEAM_RESPONSIBILITY_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_RESPONSIBILITY_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_NOTIFICATION_PREFERENCE_LIST: null,
  LOAD_CARE_TEAM_NOTIFICATION_PREFERENCE_LIST_REQUEST: null,
  LOAD_CARE_TEAM_NOTIFICATION_PREFERENCE_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_NOTIFICATION_PREFERENCE_LIST_FAILURE: null,

  CLEAR_DIRECTORY_ORGANIZATION_LIST: null,
  LOAD_DIRECTORY_ORGANIZATION_LIST_SUCCESS: null,
  LOAD_DIRECTORY_ORGANIZATION_LIST_FAILURE: null,

  CLEAR_DIRECTORY_COMMUNITY_LIST: null,
  LOAD_DIRECTORY_COMMUNITY_LIST_SUCCESS: null,
  LOAD_DIRECTORY_COMMUNITY_LIST_FAILURE: null,

  CLEAR_CLIENT_COMMUNITY_LIST: null,
  LOAD_CLIENT_COMMUNITY_LIST_SUCCESS: null,
  LOAD_CLIENT_COMMUNITY_LIST_FAILURE: null,

  CLEAR_CONTACT_COMMUNITY_LIST: null,
  LOAD_CONTACT_COMMUNITY_LIST_REQUEST: null,
  LOAD_CONTACT_COMMUNITY_LIST_SUCCESS: null,
  LOAD_CONTACT_COMMUNITY_LIST_FAILURE: null,

  CLEAR_EVENT_COMMUNITY_LIST: null,
  CLEAR_EVENT_COMMUNITY_LIST_ERROR: null,
  LOAD_EVENT_COMMUNITY_LIST_REQUEST: null,
  LOAD_EVENT_COMMUNITY_LIST_SUCCESS: null,
  LOAD_EVENT_COMMUNITY_LIST_FAILURE: null,

  CLEAR_CONTACT_STATUS_LIST: null,
  LOAD_CONTACT_STATUS_LIST_REQUEST: null,
  LOAD_CONTACT_STATUS_LIST_SUCCESS: null,
  LOAD_CONTACT_STATUS_LIST_FAILURE: null,

  CLEAR_CONTACT_SYSTEM_ROLE_LIST: null,
  CLEAR_CONTACT_SYSTEM_ROLE_LIST_ERROR: null,
  LOAD_CONTACT_SYSTEM_ROLE_LIST_REQUEST: null,
  LOAD_CONTACT_SYSTEM_ROLE_LIST_SUCCESS: null,
  LOAD_CONTACT_SYSTEM_ROLE_LIST_FAILURE: null,

  CLEAR_CONTACT_LIST_ERROR: null,

  CLEAR_CONTACT_LIST: null,

  CLEAR_CONTACT_LIST_FILTER: null,
  CHANGE_CONTACT_LIST_FILTER: null,
  CHANGE_CONTACT_LIST_FILTER_FIELD: null,
  LOAD_CONTACT_LIST_REQUEST: null,
  LOAD_CONTACT_LIST_SUCCESS: null,
  LOAD_CONTACT_LIST_FAILURE: null,
  CHANGE_CONTACT_LIST_SORTING: null,

  DOWNLOAD_CONTACT_AVATAR_REQUEST: null,
  DOWNLOAD_CONTACT_AVATAR_SUCCESS: null,
  DOWNLOAD_CONTACT_AVATAR_FAILURE: null,

  CLEAR_CONTACT_HISTORY_ERROR: null,

  CLEAR_CONTACT_HISTORY: null,

  LOAD_CONTACT_HISTORY_REQUEST: null,
  LOAD_CONTACT_HISTORY_SUCCESS: null,
  LOAD_CONTACT_HISTORY_FAILURE: null,

  CLEAR_CONTACT_COUNT: null,
  CLEAR_CONTACT_COUNT_ERROR: null,
  LOAD_CONTACT_COUNT_REQUEST: null,
  LOAD_CONTACT_COUNT_SUCCESS: null,
  LOAD_CONTACT_COUNT_FAILURE: null,

  CLEAR_CAN_ADD_CONTACT: null,
  CLEAR_CAN_ADD_CONTACT_ERROR: null,
  LOAD_CAN_ADD_CONTACT_REQUEST: null,
  LOAD_CAN_ADD_CONTACT_SUCCESS: null,
  LOAD_CAN_ADD_CONTACT_FAILURE: null,

  CLEAR_CONTACT_DETAILS: null,
  CLEAR_CONTACT_DETAILS_ERROR: null,
  LOAD_CONTACT_DETAILS_REQUEST: null,
  LOAD_CONTACT_DETAILS_SUCCESS: null,
  LOAD_CONTACT_DETAILS_FAILURE: null,

  CLEAR_CONTACT_FORM: null,
  CLEAR_CONTACT_FORM_ERROR: null,
  CLEAR_CONTACT_FORM_FIELD_ERROR: null,

  CHANGE_CONTACT_FORM_TAB: null,

  CHANGE_CONTACT_FORM_FIELD: null,
  CHANGE_CONTACT_FORM_FIELDS: null,

  VALIDATE_CONTACT_FORM: null,

  VALIDATE_CONTACT_DATA_UNIQ_REQUEST: null,
  VALIDATE_CONTACT_DATA_UNIQ_SUCCESS: null,
  VALIDATE_CONTACT_DATA_UNIQ_FAILURE: null,

  SAVE_CONTACT_REQUEST: null,
  SAVE_CONTACT_SUCCESS: null,
  SAVE_CONTACT_FAILURE: null,

  INVITE_CONTACT_REQUEST: null,
  INVITE_CONTACT_SUCCESS: null,
  INVITE_CONTACT_FAILURE: null,

  CLEAR_CLIENT_LIST_ERROR: null,
  CLEAR_CLIENT_LIST: null,
  CLEAR_CLIENT_LIST_FILTER: null,
  CHANGE_CLIENT_LIST_FILTER: null,
  CHANGE_CLIENT_LIST_FILTER_FIELD: null,
  LOAD_CLIENT_LIST_REQUEST: null,
  LOAD_CLIENT_LIST_SUCCESS: null,
  LOAD_CLIENT_LIST_FAILURE: null,
  CHANGE_CLIENT_LIST_SORTING: null,

  CLEAR_CAN_ADD_CLIENT: null,
  CLEAR_CAN_ADD_CLIENT_ERROR: null,
  LOAD_CAN_ADD_CLIENT_REQUEST: null,
  LOAD_CAN_ADD_CLIENT_SUCCESS: null,
  LOAD_CAN_ADD_CLIENT_FAILURE: null,

  CLEAR_CAN_ADD_SIGNATURE: null,
  CLEAR_CAN_ADD_SIGNATURE_ERROR: null,
  LOAD_CAN_ADD_SIGNATURE_REQUEST: null,
  LOAD_CAN_ADD_SIGNATURE_SUCCESS: null,
  LOAD_CAN_ADD_SIGNATURE_FAILURE: null,

  CLEAR_CLIENT_HISTORY_ERROR: null,
  CLEAR_CLIENT_HISTORY: null,
  LOAD_CLIENT_HISTORY_REQUEST: null,
  LOAD_CLIENT_HISTORY_SUCCESS: null,
  LOAD_CLIENT_HISTORY_FAILURE: null,

  CLEAR_CLIENT_COUNT: null,
  CLEAR_CLIENT_COUNT_ERROR: null,
  LOAD_CLIENT_COUNT_REQUEST: null,
  LOAD_CLIENT_COUNT_SUCCESS: null,
  LOAD_CLIENT_COUNT_FAILURE: null,

  CLEAR_CLIENT_DETAILS: null,
  CLEAR_CLIENT_DETAILS_ERROR: null,
  LOAD_CLIENT_DETAILS_REQUEST: null,
  LOAD_CLIENT_DETAILS_SUCCESS: null,
  LOAD_CLIENT_DETAILS_FAILURE: null,

  CLEAR_CLIENT_BILLING_DETAILS: null,
  CLEAR_CLIENT_BILLING_DETAILS_ERROR: null,

  LOAD_CLIENT_BILLING_DETAILS_REQUEST: null,
  LOAD_CLIENT_BILLING_DETAILS_SUCCESS: null,
  LOAD_CLIENT_BILLING_DETAILS_FAILURE: null,

  CLEAR_CLIENT_EMERGENCY_CONTACT_LIST_ERROR: null,
  CLEAR_CLIENT_EMERGENCY_CONTACT_LIST: null,
  LOAD_CLIENT_EMERGENCY_CONTACT_LIST_REQUEST: null,
  LOAD_CLIENT_EMERGENCY_CONTACT_LIST_SUCCESS: null,
  LOAD_CLIENT_EMERGENCY_CONTACT_LIST_FAILURE: null,

  DOWNLOAD_CLIENT_EMERGENCY_CONTACT_AVATAR_REQUEST: null,
  DOWNLOAD_CLIENT_EMERGENCY_CONTACT_AVATAR_SUCCESS: null,
  DOWNLOAD_CLIENT_EMERGENCY_CONTACT_AVATAR_FAILURE: null,

  CLEAR_CLIENT_FORM: null,
  CLEAR_CLIENT_FORM_ERROR: null,
  CLEAR_CLIENT_FORM_FIELD_ERROR: null,

  CHANGE_CLIENT_FORM_TAB: null,

  CHANGE_CLIENT_FORM_FIELD: null,
  CHANGE_CLIENT_FORM_ADDRESS_FIELD: null,

  CHANGE_CLIENT_FORM_FIELDS: null,

  SAVE_CLIENT_REQUEST: null,
  SAVE_CLIENT_SUCCESS: null,
  SAVE_CLIENT_FAILURE: null,

  CLEAR_CLIENT_STATUS_LIST: null,
  LOAD_CLIENT_STATUS_LIST_REQUEST: null,
  LOAD_CLIENT_STATUS_LIST_SUCCESS: null,
  LOAD_CLIENT_STATUS_LIST_FAILURE: null,

  VALIDATE_CLIENT_FORM: null,

  VALIDATE_CLIENT_DATA_UNIQ_REQUEST: null,
  VALIDATE_CLIENT_DATA_UNIQ_SUCCESS: null,
  VALIDATE_CLIENT_DATA_UNIQ_FAILURE: null,

  VALIDATE_CLIENT_DATA_UNIQ_IN_COMMUNITY_REQUEST: null,
  VALIDATE_CLIENT_DATA_UNIQ_IN_COMMUNITY_SUCCESS: null,
  VALIDATE_CLIENT_DATA_UNIQ_IN_COMMUNITY_FAILURE: null,

  VALIDATE_CLIENT_DATA_UNIQ_IN_ORGANIZATION_REQUEST: null,
  VALIDATE_CLIENT_DATA_UNIQ_IN_ORGANIZATION_SUCCESS: null,
  VALIDATE_CLIENT_DATA_UNIQ_IN_ORGANIZATION_FAILURE: null,

  CLEAR_CASELOAD_HISTORY_ERROR: null,
  CLEAR_CASELOAD_HISTORY: null,
  LOAD_CASELOAD_HISTORY_REQUEST: null,
  LOAD_CASELOAD_HISTORY_SUCCESS: null,
  LOAD_CASELOAD_HISTORY_FAILURE: null,

  CLEAR_CASELOAD_FORM: null,
  CLEAR_CASELOAD_FORM_ERROR: null,
  CLEAR_CASELOAD_FORM_FIELD_ERROR: null,

  CHANGE_CASELOAD_FORM_TAB: null,

  ADD_CASELOAD_FORM: null,
  REMOVE_CASELOAD_FORM: null,
  CHANGE_CASELOAD_FORM_FIELD: null,
  CHANGE_CASELOAD_FORM_FIELDS: null,
  UPDATE_CASELOAD_FORM_CLIENT_LIST: null,

  CLEAR_CASELOAD_FORM_FILTER: null,
  CHANGE_CASELOAD_FORM_FILTER: null,

  VALIDATE_CASELOAD_FORM: null,

  SAVE_CASELOAD_REQUEST: null,
  SAVE_CASELOAD_SUCCESS: null,
  SAVE_CASELOAD_FAILURE: null,

  CLEAR_ASSESSMENT_LIST_ERROR: null,
  CLEAR_ASSESSMENT_LIST: null,
  CLEAR_ASSESSMENT_LIST_FILTER: null,
  CHANGE_ASSESSMENT_LIST_FILTER: null,
  LOAD_ASSESSMENT_LIST_REQUEST: null,
  LOAD_ASSESSMENT_LIST_SUCCESS: null,
  LOAD_ASSESSMENT_LIST_FAILURE: null,
  CHANGE_ASSESSMENT_LIST_SORTING: null,

  CLEAR_ASSESSMENT_COUNT: null,
  CLEAR_ASSESSMENT_COUNT_ERROR: null,
  LOAD_ASSESSMENT_COUNT_REQUEST: null,
  LOAD_ASSESSMENT_COUNT_SUCCESS: null,
  LOAD_ASSESSMENT_COUNT_FAILURE: null,

  CLEAR_ASSESSMENT_DETAILS: null,
  CLEAR_ASSESSMENT_DETAILS_ERROR: null,
  LOAD_ASSESSMENT_DETAILS_REQUEST: null,
  LOAD_ASSESSMENT_DETAILS_SUCCESS: null,
  LOAD_ASSESSMENT_DETAILS_FAILURE: null,

  CLEAR_ASSESSMENT_SURVEY: null,
  CLEAR_ASSESSMENT_SURVEY_ERROR: null,
  LOAD_ASSESSMENT_SURVEY_REQUEST: null,
  LOAD_ASSESSMENT_SURVEY_SUCCESS: null,
  LOAD_ASSESSMENT_SURVEY_FAILURE: null,

  CLEAR_ASSESSMENT_FORM: null,
  CLEAR_ASSESSMENT_FORM_ERROR: null,
  CLEAR_ASSESSMENT_FORM_FIELD_ERROR: null,

  CHANGE_ASSESSMENT_FORM_TAB: null,

  CHANGE_ASSESSMENT_FORM_FIELD: null,
  CHANGE_ASSESSMENT_FORM_FIELDS: null,

  VALIDATE_ASSESSMENT_FORM: null,

  SAVE_ASSESSMENT_REQUEST: null,
  SAVE_ASSESSMENT_SUCCESS: null,
  SAVE_ASSESSMENT_FAILURE: null,

  CLEAR_ASSESSMENT_HISTORY_ERROR: null,

  CLEAR_ASSESSMENT_HISTORY: null,

  LOAD_ASSESSMENT_HISTORY_REQUEST: null,
  LOAD_ASSESSMENT_HISTORY_SUCCESS: null,
  LOAD_ASSESSMENT_HISTORY_FAILURE: null,

  CLEAR_ASSESSMENT_SCORE: null,
  CLEAR_ASSESSMENT_SCORE_ERROR: null,
  LOAD_ASSESSMENT_SCORE_REQUEST: null,
  LOAD_ASSESSMENT_SCORE_SUCCESS: null,
  LOAD_ASSESSMENT_SCORE_FAILURE: null,

  CLEAR_ASSESSMENT_MANAGEMENT: null,
  CLEAR_ASSESSMENT_MANAGEMENT_ERROR: null,
  LOAD_ASSESSMENT_MANAGEMENT_REQUEST: null,
  LOAD_ASSESSMENT_MANAGEMENT_SUCCESS: null,
  LOAD_ASSESSMENT_MANAGEMENT_FAILURE: null,

  CLEAR_ASSESSMENT_DIAGNOSTIC_SCALE: null,
  CLEAR_ASSESSMENT_DIAGNOSTIC_SCALE_ERROR: null,
  LOAD_ASSESSMENT_DIAGNOSTIC_SCALE_REQUEST: null,
  LOAD_ASSESSMENT_DIAGNOSTIC_SCALE_SUCCESS: null,
  LOAD_ASSESSMENT_DIAGNOSTIC_SCALE_FAILURE: null,

  CLEAR_ASSESSMENT_TYPE_LIST: null,
  CLEAR_ASSESSMENT_TYPE_LIST_ERROR: null,
  LOAD_ASSESSMENT_TYPE_LIST_REQUEST: null,
  LOAD_ASSESSMENT_TYPE_LIST_SUCCESS: null,
  LOAD_ASSESSMENT_TYPE_LIST_FAILURE: null,

  CLEAR_CAN_ADD_ASSESSMENT: null,
  CLEAR_CAN_ADD_ASSESSMENT_ERROR: null,
  LOAD_CAN_ADD_ASSESSMENT_REQUEST: null,
  LOAD_CAN_ADD_ASSESSMENT_SUCCESS: null,
  LOAD_CAN_ADD_ASSESSMENT_FAILURE: null,

  CLEAR_SERVICE_PLAN_LIST_ERROR: null,
  CLEAR_SERVICE_PLAN_LIST: null,
  CLEAR_SERVICE_PLAN_LIST_FILTER: null,
  CHANGE_SERVICE_PLAN_LIST_FILTER: null,
  CHANGE_SERVICE_PLAN_LIST_FILTER_FIELD: null,
  LOAD_SERVICE_PLAN_LIST_REQUEST: null,
  LOAD_SERVICE_PLAN_LIST_SUCCESS: null,
  LOAD_SERVICE_PLAN_LIST_FAILURE: null,
  CHANGE_SERVICE_PLAN_LIST_SORTING: null,

  CLEAR_SERVICE_PLAN_HISTORY_ERROR: null,

  CLEAR_SERVICE_PLAN_HISTORY: null,

  LOAD_SERVICE_PLAN_HISTORY_REQUEST: null,
  LOAD_SERVICE_PLAN_HISTORY_SUCCESS: null,
  LOAD_SERVICE_PLAN_HISTORY_FAILURE: null,

  CLEAR_SERVICE_PLAN_COUNT: null,
  CLEAR_SERVICE_PLAN_COUNT_ERROR: null,
  LOAD_SERVICE_PLAN_COUNT_REQUEST: null,
  LOAD_SERVICE_PLAN_COUNT_SUCCESS: null,
  LOAD_SERVICE_PLAN_COUNT_FAILURE: null,

  CLEAR_SERVICE_PLAN_DETAILS: null,
  CLEAR_SERVICE_PLAN_DETAILS_ERROR: null,
  LOAD_SERVICE_PLAN_DETAILS_REQUEST: null,
  LOAD_SERVICE_PLAN_DETAILS_SUCCESS: null,
  LOAD_SERVICE_PLAN_DETAILS_FAILURE: null,

  DOWNLOAD_SERVICE_PLAN_DETAILS_REQUEST: null,
  DOWNLOAD_SERVICE_PLAN_DETAILS_SUCCESS: null,
  DOWNLOAD_SERVICE_PLAN_DETAILS_FAILURE: null,

  CLEAR_SERVICE_PLAN_FORM: null,
  CLEAR_SERVICE_PLAN_FORM_ERROR: null,
  CLEAR_SERVICE_PLAN_FORM_FIELD_ERROR: null,

  CHANGE_SERVICE_PLAN_FORM_TAB: null,

  CHANGE_SERVICE_PLAN_FORM_FIELD: null,
  CHANGE_SERVICE_PLAN_FORM_FIELDS: null,

  VALIDATE_SERVICE_PLAN_FORM: null,
  VALIDATE_SERVICE_PLAN_NEED_SECTION: null,
  VALIDATE_SERVICE_PLAN_GOAL_SECTION: null,

  SAVE_SERVICE_PLAN_REQUEST: null,
  SAVE_SERVICE_PLAN_SUCCESS: null,
  SAVE_SERVICE_PLAN_FAILURE: null,

  ADD_SERVICE_PLAN_ANCHOR: null,
  REMOVE_SERVICE_PLAN_ANCHOR: null,
  CHANGE_SERVICE_PLAN_ANCHORS: null,

  ADD_SERVICE_PLAN_FORM_NEED: null,
  CLEAR_SERVICE_PLAN_FORM_NEED: null,
  REMOVE_SERVICE_PLAN_FORM_NEED: null,
  CHANGE_SERVICE_PLAN_FORM_NEED_FIELD: null,
  CHANGE_SERVICE_PLAN_FORM_NEED_FIELDS: null,
  CHANGE_SERVICE_PLAN_FORM_NEED_SCORE_FIELD: null,

  ADD_SERVICE_PLAN_FORM_GOAL: null,
  REMOVE_SERVICE_PLAN_FORM_GOAL: null,
  CHANGE_SERVICE_PLAN_FORM_GOAL_FIELD: null,
  CHANGE_SERVICE_PLAN_FORM_GOAL_FIELDS: null,

  CLEAR_SERVICE_STATUS_LIST: null,
  CLEAR_SERVICE_STATUS_LIST_ERROR: null,
  LOAD_SERVICE_STATUS_LIST_REQUEST: null,
  LOAD_SERVICE_STATUS_LIST_SUCCESS: null,
  LOAD_SERVICE_STATUS_LIST_FAILURE: null,

  CLEAR_SERVICE_CONTROL_REQUEST_STATUS_LIST: null,
  CLEAR_SERVICE_CONTROL_REQUEST_STATUS_LIST_ERROR: null,
  LOAD_SERVICE_CONTROL_REQUEST_STATUS_LIST_REQUEST: null,
  LOAD_SERVICE_CONTROL_REQUEST_STATUS_LIST_SUCCESS: null,
  LOAD_SERVICE_CONTROL_REQUEST_STATUS_LIST_FAILURE: null,

  CHANGE_SERVICE_PLAN_FORM_SCORE: null,

  CLEAR_CARE_CLIENT_LIST_ERROR: null,
  CLEAR_CARE_CLIENT_LIST: null,
  CLEAR_CARE_CLIENT_LIST_FILTER: null,
  CHANGE_CARE_CLIENT_LIST_FILTER: null,
  LOAD_CARE_CLIENT_LIST_REQUEST: null,
  LOAD_CARE_CLIENT_LIST_SUCCESS: null,
  LOAD_CARE_CLIENT_LIST_FAILURE: null,

  CLEAR_CARE_CLIENT_HISTORY_ERROR: null,
  CLEAR_CARE_CLIENT_HISTORY: null,
  LOAD_CARE_CLIENT_HISTORY_REQUEST: null,
  LOAD_CARE_CLIENT_HISTORY_SUCCESS: null,
  LOAD_CARE_CLIENT_HISTORY_FAILURE: null,

  CLEAR_CARE_CLIENT_COUNT: null,
  CLEAR_CARE_CLIENT_COUNT_ERROR: null,
  LOAD_CARE_CLIENT_COUNT_REQUEST: null,
  LOAD_CARE_CLIENT_COUNT_SUCCESS: null,
  LOAD_CARE_CLIENT_COUNT_FAILURE: null,

  CLEAR_CARE_CLIENT_DETAILS: null,
  CLEAR_CARE_CLIENT_DETAILS_ERROR: null,
  LOAD_CARE_CLIENT_DETAILS_REQUEST: null,
  LOAD_CARE_CLIENT_DETAILS_SUCCESS: null,
  LOAD_CARE_CLIENT_DETAILS_FAILURE: null,

  CLEAR_CARE_CLIENT_FORM: null,
  CLEAR_CARE_CLIENT_FORM_ERROR: null,
  CLEAR_CARE_CLIENT_FORM_FIELD_ERROR: null,

  CHANGE_CARE_CLIENT_FORM_TAB: null,

  CHANGE_CARE_CLIENT_FORM_FIELD: null,
  CHANGE_CARE_CLIENT_FORM_FIELDS: null,

  VALIDATE_CARE_CLIENT_FORM: null,

  SAVE_CARE_CLIENT_REQUEST: null,
  SAVE_CARE_CLIENT_SUCCESS: null,
  SAVE_CARE_CLIENT_FAILURE: null,

  DOWNLOAD_CLIENT_AVATAR_REQUEST: null,
  DOWNLOAD_CLIENT_AVATAR_SUCCESS: null,
  DOWNLOAD_CLIENT_AVATAR_FAILURE: null,

  CLEAR_CARE_TEAM_MEMBER_LIST_ERROR: null,
  CLEAR_CARE_TEAM_MEMBER_LIST: null,
  CLEAR_CARE_TEAM_MEMBER_LIST_FILTER: null,
  CHANGE_CARE_TEAM_MEMBER_LIST_FILTER: null,
  LOAD_CARE_TEAM_MEMBER_LIST_REQUEST: null,
  LOAD_CARE_TEAM_MEMBER_LIST_SUCCESS: null,
  LOAD_CARE_TEAM_MEMBER_LIST_FAILURE: null,

  CLEAR_CARE_TEAM_MEMBER_HISTORY_ERROR: null,
  CLEAR_CARE_TEAM_MEMBER_HISTORY: null,
  LOAD_CARE_TEAM_MEMBER_HISTORY_REQUEST: null,
  LOAD_CARE_TEAM_MEMBER_HISTORY_SUCCESS: null,
  LOAD_CARE_TEAM_MEMBER_HISTORY_FAILURE: null,

  CLEAR_CARE_TEAM_MEMBER_COUNT: null,
  CLEAR_CARE_TEAM_MEMBER_COUNT_ERROR: null,
  LOAD_CARE_TEAM_MEMBER_COUNT_REQUEST: null,
  LOAD_CARE_TEAM_MEMBER_COUNT_SUCCESS: null,
  LOAD_CARE_TEAM_MEMBER_COUNT_FAILURE: null,

  CLEAR_CARE_TEAM_MEMBER_DETAILS: null,
  CLEAR_CARE_TEAM_MEMBER_DETAILS_ERROR: null,
  LOAD_CARE_TEAM_MEMBER_DETAILS_REQUEST: null,
  LOAD_CARE_TEAM_MEMBER_DETAILS_SUCCESS: null,
  LOAD_CARE_TEAM_MEMBER_DETAILS_FAILURE: null,

  CLEAR_CARE_TEAM_MEMBER_FORM: null,
  CLEAR_CARE_TEAM_MEMBER_FORM_ERROR: null,

  CHANGE_CARE_TEAM_MEMBER_FORM_TAB: null,

  CHANGE_CARE_TEAM_MEMBER_FORM_FIELD: null,
  CHANGE_CARE_TEAM_MEMBER_NOTIFICATION_PREFERENCE: null,
  CHANGE_CARE_TEAM_MEMBER_NOTIFICATION_PREFERENCES: null,
  CHANGE_CARE_TEAM_MEMBER_ALL_NOTIFICATION_RESPONSIBILITIES: null,
  CHANGE_CARE_TEAM_MEMBER_ALL_NOTIFICATION_CHANNELS: null,
  CHANGE_CARE_TEAM_MEMBER_FORM_FIELDS: null,

  VALIDATE_CARE_TEAM_MEMBER_FORM: null,
  VALIDATE_CARE_TEAM_MEMBER_FORM_SUCCESS: null,
  VALIDATE_CARE_TEAM_MEMBER_FORM_FAIL: null,

  SAVE_CARE_TEAM_MEMBER_REQUEST: null,
  SAVE_CARE_TEAM_MEMBER_SUCCESS: null,
  SAVE_CARE_TEAM_MEMBER_FAILURE: null,

  CLEAR_DASHBOARD_SERVICE_PLAN_COUNT: null,
  CLEAR_DASHBOARD_SERVICE_PLAN_COUNT_ERROR: null,
  LOAD_DASHBOARD_SERVICE_PLAN_COUNT_REQUEST: null,
  LOAD_DASHBOARD_SERVICE_PLAN_COUNT_SUCCESS: null,
  LOAD_DASHBOARD_SERVICE_PLAN_COUNT_FAILURE: null,

  CLEAR_DASHBOARD_ASSESSMENT_COUNT: null,
  CLEAR_DASHBOARD_ASSESSMENT_COUNT_ERROR: null,
  LOAD_DASHBOARD_ASSESSMENT_COUNT_REQUEST: null,
  LOAD_DASHBOARD_ASSESSMENT_COUNT_SUCCESS: null,
  LOAD_DASHBOARD_ASSESSMENT_COUNT_FAILURE: null,

  CLEAR_DASHBOARD_CASELOAD_LIST: null,
  CLEAR_DASHBOARD_CASELOAD_LIST_ERROR: null,
  CLEAR_DASHBOARD_CASELOAD_LIST_FILTER: null,
  CHANGE_DASHBOARD_CASELOAD_LIST_FILTER: null,
  LOAD_DASHBOARD_CASELOAD_LIST_REQUEST: null,
  LOAD_DASHBOARD_CASELOAD_LIST_SUCCESS: null,
  LOAD_DASHBOARD_CASELOAD_LIST_FAILURE: null,

  CLEAR_DASHBOARD_CASELOAD_HISTORY: null,

  CLEAR_DASHBOARD_CASELOAD_HISTORY_ERROR: null,

  LOAD_DASHBOARD_CASELOAD_HISTORY_REQUEST: null,
  LOAD_DASHBOARD_CASELOAD_HISTORY_SUCCESS: null,
  LOAD_DASHBOARD_CASELOAD_HISTORY_FAILURE: null,

  CLEAR_DASHBOARD_APPOINTMENT_LIST: null,
  CLEAR_DASHBOARD_APPOINTMENT_LIST_ERROR: null,
  CLEAR_DASHBOARD_APPOINTMENT_LIST_FILTER: null,
  CHANGE_DASHBOARD_APPOINTMENT_LIST_FILTER: null,
  LOAD_DASHBOARD_APPOINTMENT_LIST_REQUEST: null,
  LOAD_DASHBOARD_APPOINTMENT_LIST_SUCCESS: null,
  LOAD_DASHBOARD_APPOINTMENT_LIST_FAILURE: null,

  CLEAR_DASHBOARD_APPOINTMENT_HISTORY: null,
  CLEAR_DASHBOARD_APPOINTMENT_HISTORY_ERROR: null,
  LOAD_DASHBOARD_APPOINTMENT_HISTORY_REQUEST: null,
  LOAD_DASHBOARD_APPOINTMENT_HISTORY_SUCCESS: null,
  LOAD_DASHBOARD_APPOINTMENT_HISTORY_FAILURE: null,

  CLEAR_DASHBOARD_EVENT_LIST: null,
  CLEAR_DASHBOARD_EVENT_LIST_ERROR: null,
  CLEAR_DASHBOARD_EVENT_LIST_FILTER: null,
  CHANGE_DASHBOARD_EVENT_LIST_FILTER: null,
  LOAD_DASHBOARD_EVENT_LIST_REQUEST: null,
  LOAD_DASHBOARD_EVENT_LIST_SUCCESS: null,
  LOAD_DASHBOARD_EVENT_LIST_FAILURE: null,

  CLEAR_DASHBOARD_NOTE_LIST: null,
  CLEAR_DASHBOARD_NOTE_LIST_ERROR: null,
  CLEAR_DASHBOARD_NOTE_LIST_FILTER: null,
  CHANGE_DASHBOARD_NOTE_LIST_FILTER: null,
  LOAD_DASHBOARD_NOTE_LIST_REQUEST: null,
  LOAD_DASHBOARD_NOTE_LIST_SUCCESS: null,
  LOAD_DASHBOARD_NOTE_LIST_FAILURE: null,

  LOAD_MARKETPLACE_BUILDING_LIST_REQUEST: null,
  LOAD_MARKETPLACE_BUILDING_LIST_SUCCESS: null,
  LOAD_MARKETPLACE_BUILDING_LIST_ERROR: null,
  LOAD_MARKETPLACE_BUILDING_DETAIL_SUCCESS: null,
  LOAD_MARKETPLACE_BUILDING_DETAIL_ERROR: null,
  LOAD_MARKETPLACE_BUILDING_DETAIL_REQUEST: null,
  IS_LIST_TO_DETAIL: null,
  CLEAR_BUILDING_DETAIL: null,

  LOAD_ASSOCIATIONS_LIST: null,
  ADD_ASSOCIATIONS: null,
  LOAD_ASSOCIATIONS_DETAIL: null,

  LOAD_QRCODE_SUCCESS: null,
  LOAD_ORG_DETAIL: null,
  LOAD_CATEGORIES: null,
  LOAD_COMMUNITIES: null,
  ASSOCIATEDORG_SUCCESS: null,
  LOAD_BUILDING_QRCODE: null,
  LOAD_QR_BUILDING_DETAIL: null,
  ASSOCIATEDORG_BUILDING_SUCCESS: null,
  DOWN_ORG_QRCODE: null,
  VENDOR_FORMDATA: null,
  QR_CREATE: null,
  LOAD_ALL_ORG_COMMUNITIES_QR_CODE: null,

  LOAD_MARKETPLACE_VENDOR_TYPE_SUCCESS: null,
  LOAD_MARKETPLACE_VENDOR_DETAIL_SUCESS: null,
  LOAD_MARKETPLACE_SIMPLY_NEXUS_SUCESS: null,
  LOAD_STATE_SUCCESS: null,
  LOAD_CATEGORY_SUCCESS: null,
  LOAD_ALL_VENDOR_SUCCESS: null,
  CLEAR_VENDOR_DETAIL: null,

  LOAD_MARKETPLACE_VENDOR_LIST_REQUEST: null,
  LOAD_MARKETPLACE_VENDOR_LIST_SUCCESS: null,
  LOAD_MARKETPLACE_VENDOR_LIST_ERROR: null,

  CLEAR_MARKETPLACE_COMMUNITY_LIST_ERROR: null,

  CLEAR_MARKETPLACE_COMMUNITY_LIST: null,

  CLEAR_MARKETPLACE_COMMUNITY_LIST_FILTER: null,
  CHANGE_MARKETPLACE_COMMUNITY_LIST_FILTER: null,
  CHANGE_MARKETPLACE_COMMUNITY_LIST_FILTER_FIELD: null,
  LOAD_MARKETPLACE_COMMUNITY_LIST_REQUEST: null,
  LOAD_MARKETPLACE_COMMUNITY_LIST_SUCCESS: null,
  LOAD_MARKETPLACE_COMMUNITY_LIST_FAILURE: null,

  CLEAR_MARKETPLACE_COMMUNITY_LOCATION_LIST: null,
  LOAD_MARKETPLACE_COMMUNITY_LOCATION_LIST_SUCCESS: null,
  LOAD_MARKETPLACE_COMMUNITY_LOCATION_LIST_FAILURE: null,

  CLEAR_MARKETPLACE_COMMUNITY_HISTORY_ERROR: null,

  CLEAR_MARKETPLACE_COMMUNITY_HISTORY: null,

  LOAD_MARKETPLACE_COMMUNITY_HISTORY_REQUEST: null,
  LOAD_MARKETPLACE_COMMUNITY_HISTORY_SUCCESS: null,
  LOAD_MARKETPLACE_COMMUNITY_HISTORY_FAILURE: null,

  CLEAR_MARKETPLACE_COMMUNITY_DETAILS: null,
  CLEAR_MARKETPLACE_COMMUNITY_DETAILS_ERROR: null,
  LOAD_MARKETPLACE_COMMUNITY_DETAILS_REQUEST: null,
  LOAD_MARKETPLACE_COMMUNITY_DETAILS_SUCCESS: null,
  LOAD_MARKETPLACE_COMMUNITY_DETAILS_FAILURE: null,

  SAVE_MARKETPLACE_COMMUNITY_REQUEST: null,
  SAVE_MARKETPLACE_COMMUNITY_SUCCESS: null,
  SAVE_MARKETPLACE_COMMUNITY_FAILURE: null,

  REMOVE_MARKETPLACE_COMMUNITY_REQUEST: null,
  REMOVE_MARKETPLACE_COMMUNITY_SUCCESS: null,
  REMOVE_MARKETPLACE_COMMUNITY_FAILURE: null,

  CLEAR_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM: null,
  CLEAR_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM_ERROR: null,
  CLEAR_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM_FIELD_ERROR: null,

  CHANGE_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM_TAB: null,

  CHANGE_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM_FIELD: null,
  CHANGE_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM_FIELDS: null,

  VALIDATE_MARKETPLACE_COMMUNITY_APPOINTMENT_FORM: null,

  SAVE_MARKETPLACE_COMMUNITY_APPOINTMENT_REQUEST: null,
  SAVE_MARKETPLACE_COMMUNITY_APPOINTMENT_SUCCESS: null,
  SAVE_MARKETPLACE_COMMUNITY_APPOINTMENT_FAILURE: null,

  CLEAR_MARKETPLACE_COMMUNITY_FILTER: null,
  CHANGE_MARKETPLACE_COMMUNITY_FILTER_FIELD: null,

  CLEAR_EVENT_LIST_ERROR: null,

  CLEAR_EVENT_LIST: null,

  CLEAR_EVENT_LIST_FILTER: null,
  CHANGE_EVENT_LIST_FILTER: null,
  CHANGE_EVENT_LIST_FILTER_FIELD: null,
  LOAD_EVENT_LIST_REQUEST: null,
  LOAD_EVENT_LIST_SUCCESS: null,
  LOAD_EVENT_LIST_FAILURE: null,

  CLEAR_EVENT_NOTE_LIST_ERROR: null,

  CLEAR_EVENT_NOTE_LIST: null,

  CLEAR_EVENT_NOTE_LIST_FILTER: null,
  CHANGE_EVENT_NOTE_LIST_FILTER: null,
  CHANGE_EVENT_NOTE_LIST_FILTER_FIELD: null,
  LOAD_EVENT_NOTE_LIST_REQUEST: null,
  LOAD_EVENT_NOTE_LIST_SUCCESS: null,
  LOAD_EVENT_NOTE_LIST_FAILURE: null,

  CLEAR_EVENT_NOTIFICATION_LIST_ERROR: null,

  CLEAR_EVENT_NOTIFICATION_LIST: null,

  CLEAR_EVENT_NOTIFICATION_LIST_FILTER: null,
  CHANGE_EVENT_NOTIFICATION_LIST_FILTER: null,
  CHANGE_EVENT_NOTIFICATION_LIST_FILTER_FIELD: null,
  CHANGE_EVENT_NOTIFICATION_LIST_SORTING: null,
  LOAD_EVENT_NOTIFICATION_LIST_REQUEST: null,
  LOAD_EVENT_NOTIFICATION_LIST_SUCCESS: null,
  LOAD_EVENT_NOTIFICATION_LIST_FAILURE: null,

  CLEAR_EVENT_COUNT: null,
  CLEAR_EVENT_COUNT_ERROR: null,
  LOAD_EVENT_COUNT_REQUEST: null,
  LOAD_EVENT_COUNT_SUCCESS: null,
  LOAD_EVENT_COUNT_FAILURE: null,

  CLEAR_EVENT_DETAILS: null,
  CLEAR_EVENT_DETAILS_ERROR: null,
  LOAD_EVENT_DETAILS_REQUEST: null,
  LOAD_EVENT_DETAILS_SUCCESS: null,
  LOAD_EVENT_DETAILS_FAILURE: null,

  CLEAR_CAN_ADD_EVENT: null,
  CLEAR_CAN_ADD_EVENT_ERROR: null,
  LOAD_CAN_ADD_EVENT_REQUEST: null,
  LOAD_CAN_ADD_EVENT_SUCCESS: null,
  LOAD_CAN_ADD_EVENT_FAILURE: null,

  CLEAR_CAN_ADD_NOTE: null,
  CLEAR_CAN_ADD_NOTE_ERROR: null,
  LOAD_CAN_ADD_NOTE_REQUEST: null,
  LOAD_CAN_ADD_NOTE_SUCCESS: null,
  LOAD_CAN_ADD_NOTE_FAILURE: null,

  CLEAR_CAN_ADD_EVENT_NOTE: null,
  CLEAR_CAN_ADD_EVENT_NOTE_ERROR: null,
  LOAD_CAN_ADD_EVENT_NOTE_REQUEST: null,
  LOAD_CAN_ADD_EVENT_NOTE_SUCCESS: null,
  LOAD_CAN_ADD_EVENT_NOTE_FAILURE: null,

  CLEAR_CAN_VIEW_IR: null,
  CLEAR_CAN_VIEW_IR_ERROR: null,
  LOAD_CAN_VIEW_IR_REQUEST: null,
  LOAD_CAN_VIEW_IR_SUCCESS: null,
  LOAD_CAN_VIEW_IR_FAILURE: null,

  DOWNLOAD_IR_REPORT_DOCUMENT_FAILURE: null,

  CLEAR_EVENT_FORM: null,
  CLEAR_EVENT_FORM_ERROR: null,
  CLEAR_EVENT_FORM_FIELD_ERROR: null,

  CHANGE_EVENT_FORM_TAB: null,

  CHANGE_EVENT_FORM_FIELD: null,
  CHANGE_EVENT_FORM_FIELDS: null,

  VALIDATE_EVENT_FORM: null,

  SAVE_EVENT_REQUEST: null,
  SAVE_EVENT_SUCCESS: null,
  SAVE_EVENT_FAILURE: null,

  CLEAR_EVENT_PAGE_NUMBER: null,
  CLEAR_EVENT_PAGE_NUMBER_ERROR: null,
  LOAD_EVENT_PAGE_NUMBER_REQUEST: null,
  LOAD_EVENT_PAGE_NUMBER_SUCCESS: null,
  LOAD_EVENT_PAGE_NUMBER_FAILURE: null,

  CLEAR_NOTE_PAGE_NUMBER: null,
  CLEAR_NOTE_PAGE_NUMBER_ERROR: null,
  LOAD_NOTE_PAGE_NUMBER_REQUEST: null,
  LOAD_NOTE_PAGE_NUMBER_SUCCESS: null,
  LOAD_NOTE_PAGE_NUMBER_FAILURE: null,

  CLEAR_NOT_VIEWABLE_EVENT_TYPE_LIST: null,
  CLEAR_NOT_VIEWABLE_EVENT_TYPE_LIST_ERROR: null,
  LOAD_NOT_VIEWABLE_EVENT_TYPE_LIST_REQUEST: null,
  LOAD_NOT_VIEWABLE_EVENT_TYPE_LIST_SUCCESS: null,
  LOAD_NOT_VIEWABLE_EVENT_TYPE_LIST_FAILURE: null,

  CLEAR_NOTE_LIST_ERROR: null,

  CLEAR_NOTE_LIST: null,

  CLEAR_NOTE_LIST_FILTER: null,
  CHANGE_NOTE_LIST_FILTER: null,
  LOAD_NOTE_LIST_REQUEST: null,
  LOAD_NOTE_LIST_SUCCESS: null,
  LOAD_NOTE_LIST_FAILURE: null,

  CLEAR_NOTE_HISTORY_ERROR: null,

  CLEAR_NOTE_HISTORY: null,

  LOAD_NOTE_HISTORY_REQUEST: null,
  LOAD_NOTE_HISTORY_SUCCESS: null,
  LOAD_NOTE_HISTORY_FAILURE: null,

  CLEAR_NOTE_COUNT: null,
  CLEAR_NOTE_COUNT_ERROR: null,
  LOAD_NOTE_COUNT_REQUEST: null,
  LOAD_NOTE_COUNT_SUCCESS: null,
  LOAD_NOTE_COUNT_FAILURE: null,

  CLEAR_NOTE_DETAILS: null,
  CLEAR_NOTE_DETAILS_ERROR: null,
  LOAD_NOTE_DETAILS_REQUEST: null,
  LOAD_NOTE_DETAILS_SUCCESS: null,
  LOAD_NOTE_DETAILS_FAILURE: null,

  CLEAR_HISTORY_NOTE_DETAILS: null,
  CLEAR_HISTORY_NOTE_DETAILS_ERROR: null,
  LOAD_HISTORY_NOTE_DETAILS_REQUEST: null,
  LOAD_HISTORY_NOTE_DETAILS_SUCCESS: null,
  LOAD_HISTORY_NOTE_DETAILS_FAILURE: null,

  CLEAR_NOTE_FORM: null,
  CLEAR_NOTE_FORM_ERROR: null,
  CLEAR_NOTE_FORM_FIELD: null,
  CLEAR_NOTE_FORM_FIELD_ERROR: null,

  CHANGE_NOTE_FORM_TAB: null,

  CHANGE_NOTE_FORM_FIELD: null,
  CHANGE_NOTE_FORM_FIELDS: null,

  VALIDATE_NOTE_FORM: null,

  SAVE_NOTE_REQUEST: null,
  SAVE_NOTE_SUCCESS: null,
  SAVE_NOTE_FAILURE: null,

  CLEAR_EVENT_NOTE_COMPOSED_LIST_ERROR: null,

  CLEAR_EVENT_NOTE_COMPOSED_LIST: null,

  CLEAR_EVENT_NOTE_COMPOSED_LIST_FILTER: null,
  CHANGE_EVENT_NOTE_COMPOSED_LIST_FILTER: null,
  CHANGE_EVENT_NOTE_COMPOSED_LIST_FILTER_FIELD: null,
  LOAD_EVENT_NOTE_COMPOSED_LIST_REQUEST: null,
  LOAD_EVENT_NOTE_COMPOSED_LIST_SUCCESS: null,
  LOAD_EVENT_NOTE_COMPOSED_LIST_FAILURE: null,

  CLEAR_EVENT_NOTE_COMPOSED_COUNT: null,
  CLEAR_EVENT_NOTE_COMPOSED_COUNT_ERROR: null,
  LOAD_EVENT_NOTE_COMPOSED_COUNT_REQUEST: null,
  LOAD_EVENT_NOTE_COMPOSED_COUNT_SUCCESS: null,
  LOAD_EVENT_NOTE_COMPOSED_COUNT_FAILURE: null,

  CLEAR_CLIENT_EVENT_LIST_ERROR: null,

  CLEAR_CLIENT_DOCUMENT_LIST_ERROR: null,

  CLEAR_CLIENT_DOCUMENT_LIST: null,

  CLEAR_CLIENT_DOCUMENT_LIST_FILTER: null,
  CHANGE_CLIENT_DOCUMENT_LIST_FILTER: null,
  LOAD_CLIENT_DOCUMENT_LIST_REQUEST: null,
  LOAD_CLIENT_DOCUMENT_LIST_SUCCESS: null,
  LOAD_CLIENT_DOCUMENT_LIST_FAILURE: null,

  CLEAR_CLIENT_DOCUMENT_COUNT: null,
  CLEAR_CLIENT_DOCUMENT_COUNT_ERROR: null,
  LOAD_CLIENT_DOCUMENT_COUNT_REQUEST: null,
  LOAD_CLIENT_DOCUMENT_COUNT_SUCCESS: null,
  LOAD_CLIENT_DOCUMENT_COUNT_FAILURE: null,

  CLEAR_CLIENT_DOCUMENT_DETAILS: null,
  CLEAR_CLIENT_DOCUMENT_DETAILS_ERROR: null,
  LOAD_CLIENT_DOCUMENT_DETAILS_REQUEST: null,
  LOAD_CLIENT_DOCUMENT_DETAILS_SUCCESS: null,
  LOAD_CLIENT_DOCUMENT_DETAILS_FAILURE: null,

  CLEAR_DELETE_CLIENT_DOCUMENT: null,
  CLEAR_DELETE_CLIENT_DOCUMENT_ERROR: null,
  LOAD_DELETE_CLIENT_DOCUMENT_REQUEST: null,
  LOAD_DELETE_CLIENT_DOCUMENT_SUCCESS: null,
  LOAD_DELETE_CLIENT_DOCUMENT_FAILURE: null,

  CLEAR_CLIENT_DOCUMENT_HISTORY_ERROR: null,

  CLEAR_CLIENT_DOCUMENT_HISTORY: null,

  LOAD_CLIENT_DOCUMENT_HISTORY_REQUEST: null,
  LOAD_CLIENT_DOCUMENT_HISTORY_SUCCESS: null,
  LOAD_CLIENT_DOCUMENT_HISTORY_FAILURE: null,

  CLEAR_CLIENT_DOCUMENT_FORM: null,
  CLEAR_CLIENT_DOCUMENT_FORM_ERROR: null,
  CLEAR_CLIENT_DOCUMENT_FORM_FIELD_ERROR: null,

  CHANGE_CLIENT_DOCUMENT_FORM_FIELD: null,
  CHANGE_CLIENT_DOCUMENT_FORM_FIELDS: null,

  VALIDATE_CLIENT_DOCUMENT_FORM: null,

  SAVE_CLIENT_DOCUMENT_REQUEST: null,
  SAVE_CLIENT_DOCUMENT_SUCCESS: null,
  SAVE_CLIENT_DOCUMENT_FAILURE: null,

  CLEAR_SERVICE_LIST: null,
  LOAD_SERVICE_LIST_SUCCESS: null,
  LOAD_SERVICE_LIST_FAILURE: null,

  CLEAR_INSURANCE_NETWORK_LIST: null,

  CLEAR_INSURANCE_NETWORK_LIST_FILTER: null,
  CHANGE_INSURANCE_NETWORK_LIST_FILTER: null,
  CHANGE_INSURANCE_NETWORK_LIST_FILTER_FIELD: null,

  LOAD_INSURANCE_NETWORK_LIST_REQUEST: null,
  LOAD_INSURANCE_NETWORK_LIST_SUCCESS: null,
  LOAD_INSURANCE_NETWORK_LIST_FAILURE: null,

  CLEAR_DIRECTORY_INSURANCE_NETWORK_LIST: null,

  LOAD_DIRECTORY_INSURANCE_NETWORK_LIST_REQUEST: null,
  LOAD_DIRECTORY_INSURANCE_NETWORK_LIST_SUCCESS: null,
  LOAD_DIRECTORY_INSURANCE_NETWORK_LIST_FAILURE: null,

  CLEAR_INSURANCE_PAYMENT_PLAN_LIST: null,
  LOAD_INSURANCE_PAYMENT_PLAN_LIST_REQUEST: null,
  LOAD_INSURANCE_PAYMENT_PLAN_LIST_SUCCESS: null,
  LOAD_INSURANCE_PAYMENT_PLAN_LIST_FAILURE: null,

  CLEAR_REPORT_GROUP_LIST: null,
  LOAD_REPORT_GROUP_LIST_REQUEST: null,
  LOAD_REPORT_GROUP_LIST_SUCCESS: null,
  LOAD_REPORT_GROUP_LIST_FAILURE: null,

  CLEAR_REPORT_TYPE_LIST: null,
  LOAD_REPORT_TYPE_LIST_REQUEST: null,
  LOAD_REPORT_TYPE_LIST_SUCCESS: null,
  LOAD_REPORT_TYPE_LIST_FAILURE: null,

  CLEAR_REPORT_LIST_ERROR: null,

  CLEAR_REPORT_LIST: null,
  CLEAR_REPORT_LIST_FILTER: null,
  CHANGE_REPORT_LIST_FILTER: null,
  CHANGE_REPORT_LIST_FILTER_FIELD: null,

  LOAD_REPORT_LIST_REQUEST: null,
  LOAD_REPORT_LIST_SUCCESS: null,
  LOAD_REPORT_LIST_FAILURE: null,
  VALIDATE_REPORT_FILTER: null,

  CLEAR_REPORT_DOCUMENT: null,
  CLEAR_REPORT_DOCUMENT_ERROR: null,
  DOWNLOAD_REPORT_DOCUMENT_REQUEST: null,
  DOWNLOAD_REPORT_DOCUMENT_SUCCESS: null,
  DOWNLOAD_REPORT_DOCUMENT_FAILURE: null,

  CLEAR_CAN_ADD_REPORT: null,
  CLEAR_CAN_ADD_REPORT_ERROR: null,
  LOAD_CAN_ADD_REPORT_REQUEST: null,
  LOAD_CAN_ADD_REPORT_SUCCESS: null,
  LOAD_CAN_ADD_REPORT_FAILURE: null,

  CLEAR_CAN_VIEW_SERVICE_PLAN: null,
  CLEAR_CAN_VIEW_SERVICE_PLAN_ERROR: null,
  LOAD_CAN_VIEW_SERVICE_PLAN_REQUEST: null,
  LOAD_CAN_VIEW_SERVICE_PLAN_SUCCESS: null,
  LOAD_CAN_VIEW_SERVICE_PLAN_FAILURE: null,

  CLEAR_CAN_ADD_SERVICE_PLAN: null,
  CLEAR_CAN_ADD_SERVICE_PLAN_ERROR: null,
  LOAD_CAN_ADD_SERVICE_PLAN_REQUEST: null,
  LOAD_CAN_ADD_SERVICE_PLAN_SUCCESS: null,
  LOAD_CAN_ADD_SERVICE_PLAN_FAILURE: null,

  DOWNLOAD_CARE_TEAM_MEMBER_AVATAR_REQUEST: null,
  DOWNLOAD_CARE_TEAM_MEMBER_AVATAR_SUCCESS: null,
  DOWNLOAD_CARE_TEAM_MEMBER_AVATAR_FAILURE: null,
  LOAD_DELETE_TEAM_MEMBER_DOCUMENT_REQUEST: null,
  LOAD_DELETE_TEAM_MEMBER_DOCUMENT_SUCCESS: null,
  LOAD_DELETE_TEAM_MEMBER_DOCUMENT_FAILURE: null,

  CLEAR_GROUPED_EVENT_TYPE_LIST: null,
  LOAD_GROUPED_EVENT_TYPE_LIST_SUCCESS: null,
  LOAD_GROUPED_EVENT_TYPE_LIST_FAILURE: null,
  CLEAR_CAN_ADD_CARE_TEAM_MEMBER: null,
  CLEAR_CAN_ADD_CARE_TEAM_MEMBER_ERROR: null,
  LOAD_CAN_ADD_CARE_TEAM_MEMBER_REQUEST: null,
  LOAD_CAN_ADD_CARE_TEAM_MEMBER_SUCCESS: null,
  LOAD_CAN_ADD_CARE_TEAM_MEMBER_FAILURE: null,

  CLEAR_CAN_VIEW_CARE_TEAM_MEMBER: null,
  CLEAR_CAN_VIEW_CARE_TEAM_MEMBER_ERROR: null,
  LOAD_CAN_VIEW_CARE_TEAM_MEMBER_REQUEST: null,
  LOAD_CAN_VIEW_CARE_TEAM_MEMBER_SUCCESS: null,
  LOAD_CAN_VIEW_CARE_TEAM_MEMBER_FAILURE: null,
  CHANGE_CARE_TEAM_MEMBER_LIST_SORTING: null,

  CLEAR_CAN_EDIT_CLIENT: null,
  CLEAR_CAN_EDIT_CLIENT_ERROR: null,
  LOAD_CAN_EDIT_CLIENT_REQUEST: null,
  LOAD_CAN_EDIT_CLIENT_SUCCESS: null,
  LOAD_CAN_EDIT_CLIENT_FAILURE: null,

  CLEAR_DIRECTORY_CLIENT_LIST: null,
  CLEAR_DIRECTORY_CLIENT_LIST_ERROR: null,
  LOAD_DIRECTORY_CLIENT_LIST_REQUEST: null,
  LOAD_DIRECTORY_CLIENT_LIST_SUCCESS: null,
  LOAD_DIRECTORY_CLIENT_LIST_FAILURE: null,

  CLEAR_GROUP_NOTE_FORM: null,
  CLEAR_GROUP_NOTE_FORM_ERROR: null,
  CLEAR_GROUP_NOTE_FORM_FIELD: null,
  CLEAR_GROUP_NOTE_FORM_FIELD_ERROR: null,
  CHANGE_GROUP_NOTE_FORM_FIELD: null,
  CHANGE_GROUP_NOTE_FORM_FIELDS: null,
  VALIDATE_GROUP_NOTE_FORM: null,

  CLEAR_GROUP_NOTE_DETAILS: null,
  CLEAR_GROUP_NOTE_DETAILS_ERROR: null,
  LOAD_GROUP_NOTE_DETAILS_REQUEST: null,
  LOAD_GROUP_NOTE_DETAILS_SUCCESS: null,
  LOAD_GROUP_NOTE_DETAILS_FAILURE: null,
  SAVE_GROUP_NOTE_REQUEST: null,
  SAVE_GROUP_NOTE_SUCCESS: null,

  CLEAR_CLIENT_STATUS: null,
  CLEAR_CLIENT_STATUS_ERROR: null,
  LOAD_CLIENT_STATUS_REQUEST: null,
  LOAD_CLIENT_STATUS_SUCCESS: null,
  LOAD_CLIENT_STATUS_FAILURE: null,

  CLEAR_SERVICE_PLAN_CONTROLLED: null,
  CLEAR_SERVICE_PLAN_CONTROLLED_ERROR: null,
  LOAD_SERVICE_PLAN_CONTROLLED_REQUEST: null,
  LOAD_SERVICE_PLAN_CONTROLLED_SUCCESS: null,
  LOAD_SERVICE_PLAN_CONTROLLED_FAILURE: null,

  CLEAR_SERVICE_PLAN_RESOURCE_NAMES: null,
  CLEAR_SERVICE_PLAN_RESOURCE_NAMES_ERROR: null,
  LOAD_SERVICE_PLAN_RESOURCE_NAMES_REQUEST: null,
  LOAD_SERVICE_PLAN_RESOURCE_NAMES_SUCCESS: null,
  LOAD_SERVICE_PLAN_RESOURCE_NAMES_FAILURE: null,

  CLEAR_REFERRAL_LIST_ERROR: null,

  CLEAR_REFERRAL_LIST: null,
  CLEAR_REFERRAL_LIST_FILTER: null,
  CHANGE_REFERRAL_LIST_FILTER: null,
  CHANGE_REFERRAL_LIST_FILTER_FIELD: null,

  LOAD_REFERRAL_LIST_REQUEST: null,
  LOAD_REFERRAL_LIST_SUCCESS: null,
  LOAD_REFERRAL_LIST_FAILURE: null,

  CLEAR_REFERRAL_COMMUNITY_LIST: null,
  CLEAR_REFERRAL_COMMUNITY_LIST_ERROR: null,
  LOAD_REFERRAL_COMMUNITY_LIST_REQUEST: null,
  LOAD_REFERRAL_COMMUNITY_LIST_SUCCESS: null,
  LOAD_REFERRAL_COMMUNITY_LIST_FAILURE: null,

  CLEAR_REFERRAL_DETAILS: null,
  CLEAR_REFERRAL_DETAILS_ERROR: null,
  LOAD_REFERRAL_DETAILS_REQUEST: null,
  LOAD_REFERRAL_DETAILS_SUCCESS: null,
  LOAD_REFERRAL_DETAILS_FAILURE: null,

  CLEAR_REFERRAL_RESPONSE_LIST_ERROR: null,

  CLEAR_REFERRAL_RESPONSE_LIST: null,
  CLEAR_REFERRAL_RESPONSE_LIST_FILTER: null,
  CHANGE_REFERRAL_RESPONSE_LIST_FILTER: null,
  CHANGE_REFERRAL_RESPONSE_LIST_FILTER_FIELD: null,

  LOAD_REFERRAL_RESPONSE_LIST_REQUEST: null,
  LOAD_REFERRAL_RESPONSE_LIST_SUCCESS: null,
  LOAD_REFERRAL_RESPONSE_LIST_FAILURE: null,

  CLEAR_REFERRAL_INFO_REQUEST_LIST_ERROR: null,

  CLEAR_REFERRAL_INFO_REQUEST_LIST: null,

  LOAD_REFERRAL_INFO_REQUEST_LIST_REQUEST: null,
  LOAD_REFERRAL_INFO_REQUEST_LIST_SUCCESS: null,
  LOAD_REFERRAL_INFO_REQUEST_LIST_FAILURE: null,

  CLEAR_IS_ANY_ASSESSMENT_IN_PROCESS: null,
  CLEAR_IS_ANY_ASSESSMENT_IN_PROCESS_ERROR: null,
  LOAD_IS_ANY_ASSESSMENT_IN_PROCESS_REQUEST: null,
  LOAD_IS_ANY_ASSESSMENT_IN_PROCESS_SUCCESS: null,
  LOAD_IS_ANY_ASSESSMENT_IN_PROCESS_FAILURE: null,

  // admin vendor
  LOAD_ADMIN_VENDOR_LIST_REQUEST: null,
  LOAD_ADMIN_VENDOR_LIST_SUCCESS: null,
  LOAD_ADMIN_VENDOR_LIST_ERROR: null,

  SAVE_VENDOR_FORM_REQUEST: null,
  SAVE_VENDOR_FORM_SUCCESS: null,
  SAVE_VENDOR_FORM_ERROR: null,

  // vendor companyType
  LOAD_COMPANY_TYPE_SUCCESS: null,
  LOAD_COMPANY_TYPE_ERROR: null,
  LOAD_COMPANY_TYPE_REQUEST: null,

  // vendor category
  LOAD_CATEGORY_TYPE_REQUEST: null,
  LOAD_CATEGORY_TYPE_ERROR: null,
  LOAD_CATEGORY_TYPE_SUCCESS: null,

  // vendor Detail
  VENDOR_DETAIL_QUERY: null,
  VENDOR_DETAIL_SUCCESS: null,
  VENDOR_DETAIL_ERROR: null,

  //vendor photo
  VENDOR_PHOTOS_QUERY: null,
  VENDOR_PHOTOS_SUCCESS: null,
  VENDOR_PHOTOS_ERROR: null,

  // vendor associate
  VENDOR_ASSOCIATE_COMMUNITIES_SUCCESS: null,
  VENDOR_ASSOCIATE_COMMUNITIES_QUERY: null,
  VENDOR_ASSOCIATE_COMMUNITIES_ERROR: null,

  VENDOR_CONTACT_QUERY: null,
  VENDOR_CONTACT_SUCCESS: null,
  VENDOR_CONTACT_ERROR: null,

  VENDOR_ASSOCIATE_ORGANIZATION_QUERY: null,
  VENDOR_ASSOCIATE_ORGANIZATION_SUCCESS: null,
  VENDOR_ASSOCIATE_ORGANIZATION_ERROR: null,

  CLEAR_ASSOCIATIONS_DETAIL: null,

  LOAD_CLIENT_WORKFLOW_SUCCESS: null,
  LOAD_CLIENT_WORKFLOW_CANVIEW: null,
  LOAD_CLIENT_WORKFLOW_CANADD: null,
  CREATE_CLIENT_WORKFLOW_SUCCESS: null,
  LOAD_CLIENT_WORKFLOW_COUNT_SUCCESS: null,

  EDIT_INVITED_ASSOCIATION_REQUEST: null,
  EDIT_INVITED_ASSOCIATION_SUCCESS: null,
  EDIT_INVITED_ASSOCIATION_ERROR: null,
});

export const PERIOD_UNIT_NAME = keyMirror({
  NEVER: "",
  EVERY_DAY: "",
  EVERY_WEEK: "",
  EVERY_MONTH: "",
});

export const ORGANIZATION_CATEGORY_COLORS = {
  "Dark Red": "#b71c1c",
  Red: "#e53935",
  "Dark Pink": "#e94880",
  Pink: "#f48fb1",
  Purple: "#ba68c8",
  "Deep Purple": "#9575cd",
  Indigo: "#5c6bc0",
  "Light Indigo": "#7986cb",
  "Dark blue": "#0d47a1",
  Blue: "#1e88e5",
  "Light Blue": "#4fc3f7",
  Cyan: "#16afca",
  "Dark Cyan": "#0097a7",
  Orange: "#fc7453",
  "Dark Orange": "#e7590d",
  Yellow: "#fbc02d",
  Lime: "#c0ca33",
  "Dark Lime": "#9e9d24",
  "Pale green": "#689f38",
  "Dark green": "#388e3c",
  Green: "#66bb6a",
  "Light Green": "#64dd17",
  Brown: "#8d6e63",
  Gray: "#78909c",
};

export const PROSPECT_STATUSES = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  CONVERTED_TO_CLIENT: "CONVERTED_TO_CLIENT",
};

export const HIE_CONSENT_POLICIES = {
  OPT_IN: "OPT_IN",
  OPT_OUT: "OPT_OUT",
};

/**
 *  add new screening first education
 */
export const COLONOSCOPY_DECISION_AND_EDUCATION = [
  {
    id: 1,
    title: "FIT (fecal immunochemical test)-KPWA preferred option",
    benefits: [
      {
        id: "1-1",
        title: "Can be done at home",
      },
      {
        id: "1-2",
        title: "Quick",
      },
      {
        id: "1-3",
        title: "Noninvasive. No risk of bowel tears or infections",
      },
      {
        id: "1-4",
        title: "Does not require a doctor appointment or sedation.",
      },
      {
        id: "1-5",
        title: "Requires no advance preparation, dietary modification, or loss of time from work.",
      },
      {
        id: "1-6",
        title: "Minimal handling of stool.",
      },
      {
        id: "1-7",
        title:
          "There is direc evidence that stool screening test\n" +
          "(followed by colonoscopy when positive) decreases CRC mortality.",
      },
      {
        id: "1-8",
        title: "Single specimen required",
      },
    ],
    risks: [
      {
        id: "1-0-1",
        title: "Some patient have discomfort with the thought of handling stool.",
      },
      {
        id: "1-0-2",
        title: "Colonscopy is requied if FIT is positive.",
      },
      {
        id: "1-0-3",
        title:
          "Must be done annually to be an effective screening method - adherence is important to the effectiveness of the program.",
      },
      {
        id: "1-0-4",
        title: "Cannot visually identify polyps.",
      },
    ],
  },
  {
    id: 2,
    title: "Colonoscopy-KPWA preferrd option",
    benefits: [
      {
        id: "2-1",
        title:
          "Views entire colon. Direct visualizaiton techniques offer greater sensitivity for detection of adenomas of all sizes.",
      },
      {
        id: "2-2",
        title: "Requires testing only every 10 years(presuming no polyps or other abnomalities).",
      },
      {
        id: "2-3",
        title:
          "Only screening method with the potential to preven CRC, as it allows not only for the detection but also the removal of polyps and precancerous lesions.",
      },
    ],
    risks: [
      {
        id: "2-1-1",
        title: "Requires full bowel prep. Effectiveness of colonscopy diminished if bowel prep is incomeplete.",
      },
      {
        id: "2-1-2",
        title: "Sedation needed.",
      },
      {
        id: "2-1-3",
        title: "May required loss of time from work.",
      },
      {
        id: "2-1-4",
        title: "May be associated with a potential risk of bowel tears.",
      },
      {
        id: "2-1-5",
        title: "The evidence on the benefit of colonoscpoy is indirect.",
      },
    ],
  },
  {
    id: 3,
    title: "Stool DNA test (FIT-DNA, Cologuard) - non-preferred",
    benefits: [
      {
        id: "3-1",
        title: "Has same benefits as FIT test:non-invasive, no dietary change needed, and done at home",
      },
      {
        id: "3-2",
        title: "Sensitivity may be higher than FIT.",
      },
    ],
    risks: [
      {
        id: "3-1-1",
        title:
          "Specificity is lower than FIT, resulting in more falsepositives and more follow-up colonoscopies, which increases the risk of harms.",
      },
      {
        id: "3-1-2",
        title:
          "FIT-DNA sensitivity was compared to the sensitivity of a single FIT test, rather than to 3 annual FIT tests(e.g., the recommended FIT screening frequency), so it is unknown whether the increase in sensitivity holds true in standard clinical practice.",
      },
      {
        id: "3-1-3",
        title: "No direct evidence evaluating the effect on CRC mortality.",
      },
    ],
  },
];

//  beneficiary colorectal cancer screening
export const BENEFICIARY_COLORECTAL_CANCER_SCREENING_MANAGEMENT = [
  {
    id: "1",
    degree: "Personal",
    age: "30-44yrs",
    level: 1,
    diagnosis: "Colonoscopy and referral to gastroenterology.",
  },
  {
    id: "2",
    degree: "1st degree(mom,dad,siblings)",
    age: "over 60yrs",
    level: 2,
    diagnosis: "Colonoscopy at age 40.",
  },
  {
    id: "3",
    degree: "2nd degree(grandparent, aunts/uncles, cousins)",
    age: "under 60yrs",
    level: 3,
    diagnosis: "Colonoscopy at age 40 or 10 years prior to earliest diagnosis.",
  },
  {
    id: "4",
    degree: "2nd degree",
    age: "-",
    level: 3,
    diagnosis: "Colonoscopy at age 40 or 10 years prior to earliest diagnosis.",
  },
];

export const DisadvantagesRisksList = [
  {
    id: "3-1-1",
    title: "Family hx of breast or ovarian CA.",
  },
  {
    id: "3-1-23",
    title: "Genetic mutations: BRCA 1 or BRCA 2.",
  },
  {
    id: "3-1-4",
    title: "Reproductive hx: starting menstrual cycle before 12 or menopause after 55.",
  },
  {
    id: "3-1-5",
    title: "Previous treatment with radiation to chest or breast (Hodgkin’s Lymphoma).",
  },
  {
    id: "3-1-54",
    title: "First pregnancy after 30 years old.",
  },
  {
    id: "3-1-51",
    title: "Taking hormones:  estrogen/progresterone.",
  },
  {
    id: "3-1-51",
    title: "Drinking alcohl Not being physically active.",
  },
];
